import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tablesAPI, waitlistAPI, clubsAPI, staffAPI, shiftsAPI, superAdminAPI } from '../../lib/api';
import { sanitizeTransactionNotesForDisplay } from '../../lib/transactionNotes';
import { getCachedClubLogo, setCachedClubLogo } from '../../lib/clubLogoCache';
import toast from 'react-hot-toast';
import TableBuyOutManagement from '../../components/TableBuyOutManagement';
import RakeCollection from '../../components/RakeCollection';
import { useTableBuyInOutPending } from '../../hooks/useTableBuyInOutPending';
import { getWebSocketBaseUrl } from '../../lib/websocketUrl';

/** Staff pause removes "Session Started" from notes and keeps Paused Elapsed only. */
function isStaffSessionPausedNotes(notes) {
  if (!notes || typeof notes !== 'string') return false;
  return /Paused Elapsed:\s*\d+/i.test(notes) && !/Session Started:/i.test(notes);
}

/** Staff session clock exists (aligned with backend tableHasActiveStaffSession: running or mid-pause). */
function hasStaffTableSessionStarted(notes) {
  if (!notes || typeof notes !== 'string') return false;
  return (
    /Session Started:/i.test(notes) ||
    /Paused At:/i.test(notes) ||
    /Paused Elapsed:\s*\d+/i.test(notes)
  );
}

function formatTableSessionHms(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Table-level staff session elapsed from `notes` only (matches pause/resume backend — no seatedAt fallback). */
function getTableSessionElapsedSecondsFromNotes(notes, now = new Date()) {
  if (!notes || typeof notes !== 'string') return 0;
  if (isStaffSessionPausedNotes(notes)) {
    const m = notes.match(/Paused Elapsed:\s*(\d+)/i);
    const v = m ? parseInt(m[1], 10) : 0;
    return Number.isFinite(v) && v >= 0 ? v : 0;
  }
  const startM = notes.match(/Session Started:\s*([^|]+)/i);
  if (startM?.[1]) {
    const t = new Date(String(startM[1]).trim());
    if (Number.isNaN(t.getTime())) return 0;
    const carryM = notes.match(/Paused Elapsed:\s*(\d+)/i);
    const carry = carryM ? parseInt(carryM[1], 10) : 0;
    const c = Number.isFinite(carry) && carry > 0 ? carry : 0;
    return Math.max(0, Math.floor((now.getTime() - t.getTime()) / 1000) + c);
  }
  return 0;
}

/**
 * Comprehensive Table Management Component for Super Admin
 * Features:
 * 1. Live Tables - Hologram view with seat status
 * 2. Table Management - CRUD operations
 * 3. Table Buy-In - Waitlist, seat allocation, reorder
 * 4. Table Buy-Out - Player requests, admin approval
 */
export default function TableManagement({ selectedClubId, permissions = {} }) {
  const [activeTab, setActiveTab] = useState("live-tables");
  const queryClient = useQueryClient();
  const canManageTables = permissions.canManageTables !== false;
  const canAssignSeat = permissions.canAssignSeat !== false;

  const {
    pendingBuyOutCount,
    pendingBuyOutTotal,
    pendingBuyInRequestCount,
    pendingBuyInTabBadgeCount,
  } = useTableBuyInOutPending(selectedClubId, { enableAlertSound: false });

  // Fetch tables
  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ['tables', selectedClubId],
    queryFn: () => tablesAPI.getTables(selectedClubId),
    enabled: !!selectedClubId,
  });

  // Fetch waitlist
  const { data: waitlistData, isLoading: waitlistLoading } = useQuery({
    queryKey: ['waitlist', selectedClubId],
    queryFn: () => waitlistAPI.getWaitlist(selectedClubId),
    enabled: !!selectedClubId,
  });

  const tables = tablesData || [];
  const waitlist = waitlistData || [];

  const allTabs = [
    { id: "live-tables", label: "Live Tables" },
    { id: "table-management", label: "Table Management" },
    { id: "table-buy-in", label: "Table Buy-In" },
    { id: "table-buy-out", label: "Table Buy-Out" },
    { id: "rake-collection", label: "Rake Collection" },
    { id: "history", label: "History" },
  ];
  const requestedVisibleTabs = Array.isArray(permissions.visibleTabs) ? permissions.visibleTabs : null;
  const tabs = requestedVisibleTabs
    ? allTabs.filter((tab) => requestedVisibleTabs.includes(tab.id))
    : allTabs;

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "live-tables");
    }
  }, [tabs, activeTab]);

  return (
    <div className="text-white space-y-6">
      <h1 className="text-3xl font-bold">Tables & Waitlist</h1>

      {!selectedClubId && (
        <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 text-yellow-100 flex items-center gap-3">
          <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.3 2.647-1.3 3.412 0l7 12a1.5 1.5 0 01-1.302 2.25H2.847a1.5 1.5 0 01-1.302-2.25l7-12zM9 13a1 1 0 112 0v2a1 1 0 11-2 0v-2zm0-6a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
          <p className="font-medium">Please select a club from the sidebar dropdown to manage tables.</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 flex-wrap">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          let title;
          let content = tab.label;

          if (selectedClubId && tab.id === "table-buy-in") {
            title =
              pendingBuyInTabBadgeCount > 0
                ? `${pendingBuyInTabBadgeCount} on waitlist${
                    pendingBuyInRequestCount > 0
                      ? ` · ${pendingBuyInRequestCount} seated table chip request(s) (cashier/sidebar)`
                      : ""
                  }`
                : pendingBuyInRequestCount > 0
                  ? `${pendingBuyInRequestCount} seated table chip buy-in request(s) — use Cashier or sidebar alert`
                  : undefined;
            content =
              pendingBuyInTabBadgeCount > 0 ? (
                <span className="inline-flex items-center gap-2">
                  <span>Table Buy-In</span>
                  <span
                    className={`text-xs font-bold rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center flex-shrink-0 text-white shadow-sm ${
                      isActive
                        ? "bg-red-500 ring-2 ring-red-300/80"
                        : "bg-red-600"
                    }`}
                  >
                    {pendingBuyInTabBadgeCount > 99 ? "99+" : pendingBuyInTabBadgeCount}
                  </span>
                </span>
              ) : (
                tab.label
              );
          } else if (selectedClubId && tab.id === "table-buy-out") {
            title =
              pendingBuyOutCount > 0
                ? `${pendingBuyOutCount} pending · ₹${pendingBuyOutTotal.toLocaleString("en-IN")} est. on table`
                : undefined;
            content =
              pendingBuyOutCount > 0 ? (
                <span className="inline-flex items-center gap-2">
                  <span>Table Buy-Out</span>
                  <span
                    className={`text-xs font-bold rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center flex-shrink-0 text-white shadow-sm ${
                      isActive
                        ? "bg-red-500 ring-2 ring-red-300/80"
                        : "bg-red-600"
                    }`}
                  >
                    {pendingBuyOutCount > 99 ? "99+" : pendingBuyOutCount}
                  </span>
                </span>
              ) : (
                tab.label
              );
          }

          return (
            <button
              key={tab.id}
              type="button"
              title={title}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-semibold transition-all ${
                isActive
                  ? "bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-b-2 border-blue-400"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {content}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Tab 1: Live Tables */}
        {activeTab === "live-tables" && (
          <LiveTablesView 
            selectedClubId={selectedClubId}
            tables={tables}
            tablesLoading={tablesLoading}
            canManageTables={canManageTables}
          />
        )}

        {/* Tab 2: Table Management */}
        {activeTab === "table-management" && (
          <TableManagementView
            selectedClubId={selectedClubId}
            tables={tables}
            tablesLoading={tablesLoading}
            canManageTables={canManageTables}
          />
        )}

        {/* Tab 3: Table Buy-In */}
        {activeTab === "table-buy-in" && (
          <TableBuyInView
            selectedClubId={selectedClubId}
            tables={tables}
            waitlist={waitlist}
            waitlistLoading={waitlistLoading}
            canAssignSeat={canAssignSeat}
          />
        )}

        {/* Tab 4: Table Buy-Out */}
        {activeTab === "table-buy-out" && (
          <TableBuyOutView
            selectedClubId={selectedClubId}
            tables={tables}
          />
        )}

        {/* Tab 5: Rake Collection */}
        {activeTab === "rake-collection" && (
          <RakeCollection clubId={selectedClubId} gameType="poker" />
        )}
        
        {/* Tab 6: History */}
        {activeTab === "history" && (
          <HistoryView
            selectedClubId={selectedClubId}
            tables={tables}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Live Tables View Component
// ============================================================================
function LiveTablesView({ selectedClubId, tables, tablesLoading, canManageTables = true }) {
  const [selectedTable, setSelectedTable] = useState(null);
  const [showSessionControl, setShowSessionControl] = useState(false);
  const [selectedTableForControl, setSelectedTableForControl] = useState(null);
  const [sessionParams, setSessionParams] = useState({
    minPlayTime: 30,
    callTime: 5,
    cashOutWindow: 10,
    sessionTimeout: 120,
  });
  const [sessionStatus, setSessionStatus] = useState('Active');

  // Filter: Only non-Rummy tables, active status
  const activeTables = tables.filter(t => 
    (t.status === 'AVAILABLE' || t.status === 'OCCUPIED') &&
    t.tableType !== 'RUMMY'
  );
  const startedSessionTables = activeTables.filter((t) => hasStaffTableSessionStarted(t.notes));
  const sessionControlTable =
    showSessionControl && selectedTableForControl && Array.isArray(tables)
      ? tables.find((t) => t.id === selectedTableForControl.id) || selectedTableForControl
      : selectedTableForControl;

  // Helper function to extract dealer name from notes
  const getDealerName = (table) => {
    if (!table.notes) return null;
    const dealerMatch = table.notes.match(/Dealer:.*?\(([^)]+)\)/i);
    return dealerMatch ? dealerMatch[1] : null;
  };

  const handleEditSession = (table) => {
    setSelectedTableForControl(table);
    setShowSessionControl(true);
    
    // Parse session parameters from notes if available
    const noteParts = table.notes ? table.notes.split('|').map(p => p.trim()) : [];
    const minPlayTime = noteParts[3]?.match(/\d+/)?.[0] || '30';
    const callTime = noteParts[4]?.match(/\d+/)?.[0] || '5';
    const cashOutWindow = noteParts[5]?.match(/\d+/)?.[0] || '10';
    const sessionTimeout = noteParts[6]?.match(/\d+/)?.[0] || '120';
    
    setSessionParams({
      minPlayTime: parseInt(minPlayTime),
      callTime: parseInt(callTime),
      cashOutWindow: parseInt(cashOutWindow),
      sessionTimeout: parseInt(sessionTimeout),
    });

    // Pause/resume uses table notes, not OCCUPIED vs AVAILABLE (both can be "running").
    setSessionStatus(isStaffSessionPausedNotes(table.notes) ? 'Paused' : 'Active');
  };

  return (
    <div className="space-y-6">
      {/* Table Session Control Panel */}
      {showSessionControl && selectedTableForControl && (
        <TableSessionControl
          table={sessionControlTable}
          tables={startedSessionTables}
          sessionParams={sessionParams}
          setSessionParams={setSessionParams}
          sessionStatus={sessionStatus}
          setSessionStatus={setSessionStatus}
          clubId={selectedClubId}
          onClose={() => {
            setShowSessionControl(false);
            setSelectedTableForControl(null);
          }}
          onSelectTable={(table) => {
            setSelectedTableForControl(table);
            handleEditSession(table);
          }}
        />
      )}

      {!showSessionControl && (
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Live Tables - Hologram View</h2>
            <p className="text-gray-400 text-sm mt-1">Manage live tables, seat players, and handle buy-ins using table hologram.</p>
          </div>
          <div className="bg-slate-700 px-4 py-2 rounded-lg">
            <div className="text-sm text-gray-300">Live sessions: {startedSessionTables.length}</div>
            <div className="text-sm text-gray-300">Total Players: {startedSessionTables.reduce((sum, t) => sum + (t.currentSeats || 0), 0)}</div>
          </div>
        </div>

        {tablesLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p>Loading tables...</p>
          </div>
        ) : startedSessionTables.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎲</div>
            <p className="text-xl text-gray-300">No live sessions</p>
            <p className="text-gray-400 text-sm mt-2">Start a session from Table Management — only tables with a running or paused session appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {startedSessionTables.map((table) => (
              <div
                key={table.id}
                className="bg-slate-700 rounded-xl p-5 border border-slate-600 hover:border-blue-500 transition-all cursor-pointer"
                onClick={() => setSelectedTable(table)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-white">Table {table.tableNumber}</h3>
                    <p className="text-sm text-gray-400">{table.tableType}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    table.status === 'OCCUPIED' ? 'bg-green-600/20 text-green-400' : 'bg-blue-600/20 text-blue-400'
                  }`}>
                    {table.status}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Stakes:</span>
                    <span className="text-white font-medium">₹{table.minBuyIn || 0}/₹{table.maxBuyIn || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Occupied Seats:</span>
                    <span className="text-white font-medium">{table.currentSeats || 0} / {table.maxSeats}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Available Seats:</span>
                    <span className="text-green-400 font-medium">{table.maxSeats - (table.currentSeats || 0)}</span>
                  </div>
                  {getDealerName(table) && (
                    <div className="flex justify-between text-sm pt-1 border-t border-slate-600">
                      <span className="text-gray-400">👤 Dealer:</span>
                      <span className="text-purple-400 font-semibold">{getDealerName(table)}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTable(table);
                  }}
                  className="w-full mt-4 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <span>🎯</span>
                  <span>View Table Hologram</span>
                </button>
                
                {canManageTables && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditSession(table);
                    }}
                    className="w-full mt-2 bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-500 hover:to-pink-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <span>⚙️</span>
                    <span>Edit Session Control</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Table Hologram Modal */}
      {selectedTable && (
        <TableHologramModal
          table={selectedTable}
          onClose={() => setSelectedTable(null)}
          clubId={selectedClubId}
        />
      )}
    </div>
  );
}

// ============================================================================
// Table Session Control Component
// ============================================================================
function TableSessionControl({ 
  table, 
  tables, 
  sessionParams, 
  setSessionParams, 
  sessionStatus, 
  setSessionStatus,
  clubId,
  onClose,
  onSelectTable 
}) {
  const queryClient = useQueryClient();
  const [selectedClubId, setSelectedClubId] = useState(clubId);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [seatedPlayers, setSeatedPlayers] = useState([]);
  const [settlements, setSettlements] = useState({});
  const [rakeAmount, setRakeAmount] = useState('');
  const [tableSessionHms, setTableSessionHms] = useState('00:00:00');

  // Update selectedClubId when clubId prop changes
  useEffect(() => {
    if (clubId) {
      setSelectedClubId(clubId);
    }
  }, [clubId]);

  useEffect(() => {
    if (!table?.notes) {
      setSessionStatus('Active');
      return;
    }
    setSessionStatus(isStaffSessionPausedNotes(table.notes) ? 'Paused' : 'Active');
  }, [table?.id, table?.notes, setSessionStatus]);

  useEffect(() => {
    if (!table) return;
    const tick = () =>
      setTableSessionHms(formatTableSessionHms(getTableSessionElapsedSecondsFromNotes(table.notes)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [table?.id, table?.notes]);

  // Pause session mutation
  const pauseSessionMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.pauseSession(selectedClubId, table.id);
    },
    onSuccess: () => {
      setSessionStatus('Paused');
      toast.success('Session paused successfully');
      queryClient.invalidateQueries(['tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to pause session');
    },
  });

  // Resume session mutation
  const resumeSessionMutation = useMutation({
    mutationFn: async () => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.resumeSession(selectedClubId, table.id);
    },
    onSuccess: () => {
      setSessionStatus('Active');
      toast.success('Session resumed successfully');
      queryClient.invalidateQueries(['tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resume session');
    },
  });

  // Settle and end session mutation
  const settleAndEndMutation = useMutation({
    mutationFn: async (settlementsData) => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.settleAndEndSession(selectedClubId, table.id, settlementsData);
    },
    onSuccess: () => {
      toast.success('All players settled and session ended successfully!');
      queryClient.invalidateQueries(['tables', selectedClubId]);
      setShowSettlementModal(false);
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to settle and end session');
    },
  });

  // Update session params mutation
  const updateParamsMutation = useMutation({
    mutationFn: async (params) => {
      if (!selectedClubId) throw new Error('Club ID not found');
      return await tablesAPI.updateSessionParams(selectedClubId, table.id, params);
    },
    onSuccess: (data) => {
      toast.success('Session parameters updated successfully');
      queryClient.invalidateQueries(['tables', selectedClubId]);
      if (data.sessionParams) {
        setSessionParams(data.sessionParams);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update session parameters');
    },
  });

  const handlePauseSession = () => {
    const started = hasStaffTableSessionStarted(table.notes);
    if (!started) {
      resumeSessionMutation.mutate();
      return;
    }
    if (sessionStatus === 'Active') {
      pauseSessionMutation.mutate();
    } else {
      resumeSessionMutation.mutate();
    }
  };

  const handleEndSession = async () => {
    if (!selectedClubId) {
      toast.error('Club ID not found');
      return;
    }

    try {
      // Fetch seated players for this table
      const response = await tablesAPI.getSeatedPlayersForTable(selectedClubId, table.id);
      
      if (response.seatedPlayers && response.seatedPlayers.length > 0) {
        // Show settlement modal if there are seated players
        setSeatedPlayers(response.seatedPlayers);
        
        // Initialize settlements with 0 for each player
        const initialSettlements = {};
        response.seatedPlayers.forEach(player => {
          initialSettlements[player.playerId] = 0;
        });
        setSettlements(initialSettlements);
        setShowSettlementModal(true);
      } else {
        // No seated players, end session directly
        if (window.confirm('No players are seated. End this session?')) {
          await tablesAPI.endSession(selectedClubId, table.id);
          toast.success('Session ended successfully');
          queryClient.invalidateQueries(['tables', selectedClubId]);
          onClose();
        }
      }
    } catch (error) {
      console.error('Error fetching seated players:', error);
      toast.error('Failed to fetch seated players');
    }
  };

  const handleSettlementChange = (playerId, amount) => {
    setSettlements(prev => ({
      ...prev,
      [playerId]: parseFloat(amount) || 0
    }));
  };

  const handleConfirmSettlement = () => {
    // Convert settlements object to array format
    const settlementsArray = Object.entries(settlements).map(([playerId, amount]) => ({
      playerId,
      amount: parseFloat(amount) || 0
    }));

    const rakeNum = parseFloat(rakeAmount) || 0;

    // Confirm with admin
    const totalAmount = settlementsArray.reduce((sum, s) => sum + s.amount, 0);
    const confirmMsg = rakeNum > 0
      ? `Settle ${settlementsArray.length} players for a total of ₹${totalAmount} with ₹${rakeNum} rake? This will end the session.`
      : `Settle ${settlementsArray.length} players for a total of ₹${totalAmount}? This will end the session.`;

    if (window.confirm(confirmMsg)) {
      settleAndEndMutation.mutate({ settlements: settlementsArray, rakeAmount: rakeNum || undefined });
    }
  };

  const handleSetParameter = (param, value) => {
    const params = { [param]: parseInt(value) };
    updateParamsMutation.mutate(params);
  };

  const getTableGameType = (t) => {
    const noteParts = t.notes ? t.notes.split('|').map(p => p.trim()) : [];
    return noteParts[1] || t.tableType;
  };

  const getTableStakes = (t) => {
    const noteParts = t.notes ? t.notes.split('|').map(p => p.trim()) : [];
    return noteParts[2]?.replace('Stakes:', '').trim() || `₹${t.minBuyIn || 0}/₹${t.maxBuyIn || 0}`;
  };

  return (
    <div className="bg-gradient-to-br from-blue-900/50 via-purple-900/50 to-indigo-900/50 rounded-2xl p-8 border-2 border-blue-500/30 shadow-2xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-white">Table Session Control</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Table Sessions */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Table Sessions</h3>
          <div className="space-y-3">
            {tables.length === 0 ? (
              <p className="text-sm text-gray-400">
                No tables with a started staff session. Start a session with Resume on a table first.
              </p>
            ) : (
              tables.map((t) => (
              <div
                key={t.id}
                onClick={() => onSelectTable(t)}
                className={`p-4 rounded-lg cursor-pointer transition-all ${
                  t.id === table.id
                    ? 'bg-blue-600/30 border-2 border-blue-500'
                    : 'bg-slate-700/50 border border-slate-600 hover:bg-slate-700'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-white">Table {t.tableNumber} - {getTableGameType(t).split(' | ')[0]}</div>
                    <div className="text-sm text-gray-400">{getTableGameType(t)}</div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    isStaffSessionPausedNotes(t.notes)
                      ? 'bg-yellow-600/20 text-yellow-400'
                      : 'bg-green-600/20 text-green-400'
                  }`}>
                    {isStaffSessionPausedNotes(t.notes) ? 'Paused' : 'Running'}
                  </span>
                </div>
              </div>
            ))
            )}
          </div>

          {/* Selected Table Details */}
          <div className="mt-6 p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg">
            <div className="text-sm text-blue-300 mb-2">Selected: Table {table.tableNumber} - {getTableGameType(table).split(' | ')[0]}</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400">Game:</span>
                <span className="text-white ml-2">{getTableGameType(table)}</span>
              </div>
              <div>
                <span className="text-gray-400">Stakes:</span>
                <span className="text-white ml-2">{getTableStakes(table)}</span>
              </div>
            </div>
            <div className="text-sm mt-2">
              <span className="text-gray-400">Players:</span>
              <span className="text-white ml-2">{table.currentSeats || 0}/{table.maxSeats}</span>
            </div>
          </div>
        </div>

        {/* Center: Session Actions */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Session Actions</h3>
          
          <div className="mb-6">
            <div className="text-gray-400 text-sm mb-2">Current Status</div>
            <div className={`text-4xl font-bold ${sessionStatus === 'Active' ? 'text-green-400' : 'text-yellow-400'}`}>
              {sessionStatus}
            </div>
            <div className="mt-5 rounded-xl bg-slate-900/60 border border-slate-600/80 px-4 py-3 text-center">
              <div className="text-gray-500 text-xs uppercase tracking-wide">Table session time</div>
              <div className="text-2xl font-mono font-bold text-white mt-1">{tableSessionHms}</div>
              <div className="text-xs text-gray-500 mt-1">Same clock as hologram · freezes when paused</div>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handlePauseSession}
              className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2"
            >
              <span>
                {!hasStaffTableSessionStarted(table.notes)
                  ? '▶'
                  : sessionStatus === 'Active'
                    ? '⏸'
                    : '▶'}
              </span>
              <span>
                {!hasStaffTableSessionStarted(table.notes)
                  ? 'Start Table Session'
                  : sessionStatus === 'Active'
                    ? 'Pause Session'
                    : 'Resume Session'}
              </span>
            </button>

            <button
              onClick={handleEndSession}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2"
            >
              <span>⬛</span>
              <span>End Session</span>
            </button>
          </div>

          <div className="mt-6 p-4 bg-slate-700/50 rounded-lg">
            <p className="text-sm text-gray-400 text-center">
              Session parameters can be modified on the right panel.
            </p>
          </div>
        </div>

        {/* Right: Timing & Parameters */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold text-white mb-4">Timing & Parameters</h3>
          
          <div className="space-y-6">
            {/* Min Play Time */}
            <div>
              <div className="text-gray-400 text-sm font-medium mb-2">MIN PLAY TIME</div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={sessionParams.minPlayTime}
                  onChange={(e) => setSessionParams({ ...sessionParams, minPlayTime: e.target.value })}
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-semibold"
                  min="30"
                />
                <button
                  onClick={() => handleSetParameter('minPlayTime', sessionParams.minPlayTime)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-semibold transition-colors"
                >
                  Set
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">Current: {sessionParams.minPlayTime} mins</div>
            </div>

            {/* Call Time */}
            <div>
              <div className="text-gray-400 text-sm font-medium mb-2">CALL TIME</div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={sessionParams.callTime}
                  onChange={(e) => setSessionParams({ ...sessionParams, callTime: e.target.value })}
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-semibold"
                  min="1"
                />
                <button
                  onClick={() => handleSetParameter('callTime', sessionParams.callTime)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-semibold transition-colors"
                >
                  Set
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">Current: {sessionParams.callTime} mins</div>
            </div>

            {/* Cash-out Window */}
            <div>
              <div className="text-gray-400 text-sm font-medium mb-2">CASH-OUT WINDOW</div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={sessionParams.cashOutWindow}
                  onChange={(e) => setSessionParams({ ...sessionParams, cashOutWindow: e.target.value })}
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-semibold"
                  min="1"
                />
                <button
                  onClick={() => handleSetParameter('cashOutWindow', sessionParams.cashOutWindow)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-semibold transition-colors"
                >
                  Set
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">Current: {sessionParams.cashOutWindow} mins</div>
            </div>

            {/* Session Timeout */}
            <div>
              <div className="text-gray-400 text-sm font-medium mb-2">SESSION TIMEOUT</div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={sessionParams.sessionTimeout}
                  onChange={(e) => setSessionParams({ ...sessionParams, sessionTimeout: e.target.value })}
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-semibold"
                  min="30"
                />
                <button
                  onClick={() => handleSetParameter('sessionTimeout', sessionParams.sessionTimeout)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-semibold transition-colors"
                >
                  Set
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">Current: {sessionParams.sessionTimeout} mins</div>
            </div>
          </div>
        </div>
      </div>

      {/* Settlement Modal */}
      {showSettlementModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-red-500/50 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">💰 Settle All Players</h3>
              <button
                onClick={() => setShowSettlementModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6 p-4 bg-red-600/20 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300">
                ⚠️ Enter the cash-out amount for each seated player. This will create transactions,
                update balances, unseat all players, and end the session.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {seatedPlayers.map(player => (
                <div key={player.playerId} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-white">{player.playerName}</div>
                      <div className="text-sm text-gray-400">Seat #{player.seatNumber}</div>
                    </div>
                    <div className="text-sm text-gray-400">
                      Seated {new Date(player.seatedAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
                    </div>
                  </div>
                  {/* Player Balance Info */}
                  <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
                    <div className="bg-slate-800/60 rounded px-3 py-2 text-center">
                      <div className="text-gray-400 text-xs">Buy-In</div>
                      <div className="text-cyan-400 font-semibold">₹{(player.buyInAmount || 0).toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-800/60 rounded px-3 py-2 text-center">
                      <div className="text-gray-400 text-xs">Wallet Balance</div>
                      <div className={`font-semibold ${(player.walletBalance || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ₹{(player.walletBalance || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-800/60 rounded px-3 py-2 text-center">
                      <div className="text-gray-400 text-xs">Credits</div>
                      <div className="text-yellow-400 font-semibold">
                        {(player.totalCredits || 0) > 0 ? `₹${(player.totalCredits || 0).toLocaleString()}` : '-'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">Cash-out Amount:</span>
                    <input
                      type="number"
                      value={settlements[player.playerId] || 0}
                      onChange={(e) => handleSettlementChange(player.playerId, e.target.value)}
                      className="flex-1 px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-lg font-semibold"
                      placeholder="Enter amount"
                      min="0"
                      step="100"
                    />
                    <span className="text-white font-semibold">₹</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg mb-4">
              <span className="text-blue-300 font-semibold">Total Settlement:</span>
              <span className="text-white text-2xl font-bold">
                ₹{Object.values(settlements).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0).toLocaleString()}
              </span>
            </div>

            {/* Rake Collection Input */}
            <div className="p-4 bg-amber-600/20 border border-amber-500/30 rounded-lg mb-6">
              <div className="flex items-center gap-3">
                <span className="text-amber-300 font-semibold whitespace-nowrap">🎰 Session Rake:</span>
                <input
                  type="number"
                  value={rakeAmount}
                  onChange={(e) => setRakeAmount(e.target.value)}
                  className="flex-1 px-4 py-2 bg-slate-600 border border-amber-500/50 rounded-lg text-white text-lg font-semibold"
                  placeholder="Enter rake amount (optional)"
                  min="0"
                  step="100"
                />
                <span className="text-white font-semibold">₹</span>
              </div>
              <p className="text-amber-300/70 text-xs mt-2">Rake will be recorded for this session automatically</p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowSettlementModal(false)}
                className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSettlement}
                disabled={settleAndEndMutation.isLoading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
              >
                {settleAndEndMutation.isLoading ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>✅</span>
                    <span>Settle & End Session</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Table Hologram Modal Component
// ============================================================================
function TableHologramModal({ table: initialTable, onClose, clubId }) {
  const queryClient = useQueryClient();
  // Seed from localStorage cache so the logo paints on first render — no spade flicker.
  const [clubData, setClubData] = useState(() => {
    const cached = getCachedClubLogo(clubId);
    return cached ? { logoUrl: cached } : null;
  });
  const [clubFetched, setClubFetched] = useState(false);
  const [sessionTime, setSessionTime] = useState('00:00:00');
  const [seatedPlayersData, setSeatedPlayersData] = useState([]);
  const [activeTab, setActiveTab] = useState('view'); // 'view' or 'history'
  const [tableHistory, setTableHistory] = useState([]);
  /** Live row (WebSocket merges session notes after pause/resume). */
  const [table, setTable] = useState(initialTable);
  useEffect(() => {
    setTable(initialTable);
  }, [initialTable]);
  
  // Extract dealer info from table notes
  const getDealerInfo = () => {
    if (!table.notes) return null;
    const dealerMatch = table.notes.match(/Dealer:\s*([a-f0-9-]{36})\s*\(([^)]+)\)/i);
    if (dealerMatch) {
      return { id: dealerMatch[1], name: dealerMatch[2] };
    }
    return null;
  };

  const dealerInfo = getDealerInfo();
  
  // Fetch seated players for this table
  useEffect(() => {
    const fetchSeatedPlayers = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3333/api'}/clubs/${clubId}/tables/${table.id}/seated-players`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-tenant-id': tenantId || '',
            'x-club-id': clubId,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setSeatedPlayersData(data.seatedPlayers || []);
        }
      } catch (error) {
        console.error('Error fetching seated players:', error);
      }
    };
    
    if (clubId && table?.id) {
      fetchSeatedPlayers();

      // Drive updates via WebSocket so there is no polling.
      const wsBase = getWebSocketBaseUrl();
      if (!wsBase) return;
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token') || sessionStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const { io: socketIO } = require('socket.io-client');
      const socket = socketIO(`${wsBase}/realtime`, {
        auth: { clubId, userId, token },
        transports: ['websocket', 'polling'],
        reconnection: true,
      });
      socket.on('connect', () => socket.emit('subscribe:club', { clubId, userId }));
      socket.on('table:status-changed', (payload) => {
        if (payload?.table?.id === initialTable.id) {
          setTable((prev) => ({
            ...prev,
            ...payload.table,
            notes: payload.table?.notes != null ? payload.table.notes : prev.notes,
          }));
        }
        fetchSeatedPlayers();
      });
      socket.on('tables:updated', () => {
        queryClient.invalidateQueries(['tables', clubId]);
        fetchSeatedPlayers();
      });
      socket.on('waitlist:status-changed', fetchSeatedPlayers);
      socket.on('waitlist:position-updated', fetchSeatedPlayers);
      socket.on('transaction:new', fetchSeatedPlayers);
      socket.on('balance:updated', fetchSeatedPlayers);
      socket.on('buyin:updated', fetchSeatedPlayers);
      return () => { socket.disconnect(); };
    }
  }, [clubId, table?.id, queryClient]);

  // Fetch table history (buy-ins and buy-outs)
  useEffect(() => {
    const fetchTableHistory = async () => {
      if (activeTab !== 'history') return;
      
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3333/api'}/clubs/${clubId}/transactions?tableNumber=${table.tableNumber}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-tenant-id': tenantId || '',
            'x-club-id': clubId,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          // Filter for table-related transactions (Table Buy In, Table Buy Out, Club Buy In, Credit, Debit)
          const filtered = data.filter(t => 
            (t.type === 'Table Buy In' || t.type === 'Table Buy Out' || t.type === 'Club Buy In' || 
             t.type === 'Buy In' || t.type === 'Credit' || t.type === 'Debit' ||
             (t.type === 'Deposit' && t.notes?.includes(`Table ${table.tableNumber}`))) &&
            (t.notes?.includes(`Table ${table.tableNumber}`) || t.description?.includes(`Table ${table.tableNumber}`))
          ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setTableHistory(filtered);
        }
      } catch (error) {
        console.error('Error fetching table history:', error);
      }
    };
    
    if (clubId && table?.tableNumber && activeTab === 'history') {
      fetchTableHistory();
    }
  }, [clubId, table?.tableNumber, activeTab]);

  // Unassign dealer mutation
  const unassignDealerMutation = useMutation({
    mutationFn: async () => {
      if (!table || !dealerInfo) return;
      
      // Remove dealer info from notes
      const existingNotes = table.notes || '';
      const updatedNotes = existingNotes.replace(/\s*\|\s*Dealer:.*$/i, '').trim();
      
      return tablesAPI.updateTable(clubId, table.id, {
        notes: updatedNotes
      });
    },
    onSuccess: () => {
      toast.success('Dealer unassigned successfully');
      queryClient.invalidateQueries(['tables', clubId]);
      queryClient.invalidateQueries(['staff', clubId]);
      queryClient.invalidateQueries(['all-tables', clubId]);
      queryClient.invalidateQueries(['rummy-tables', clubId]);
      onClose(); // Close modal to refresh
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to unassign dealer');
    },
  });

  const handleUnassignDealer = () => {
    if (window.confirm(`Are you sure you want to unassign dealer "${dealerInfo.name}" from this table?`)) {
      unassignDealerMutation.mutate();
    }
  };
  
  // Fetch club data to get logo
  useEffect(() => {
    const fetchClubData = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3333/api'}/clubs/${clubId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-tenant-id': tenantId || '',
            'x-club-id': clubId,
          },
        });
        if (response.ok) {
          const data = await response.json();

          setClubData(data);
          setCachedClubLogo(clubId, data?.logoUrl || null);
        } else {
          console.error('Failed to fetch club data:', response.status, await response.text());
        }
      } catch (error) {
        console.error('Error fetching club data:', error);
      } finally {
        setClubFetched(true);
      }
    };
    if (clubId) {
      fetchClubData();
    }
  }, [clubId]);

  // Session timer — staff notes only (never earliest seatedAt: that caused random resets vs live session).
  useEffect(() => {
    if (!table) {
      setSessionTime('00:00:00');
      return;
    }
    const tick = () => {
      setSessionTime(formatTableSessionHms(getTableSessionElapsedSecondsFromNotes(table.notes)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [table?.id, table?.notes]);

  const seats = Array.from({ length: table.maxSeats }, (_, i) => i + 1);
  
  // Get occupied seats from seated players data
  const occupiedSeats = seatedPlayersData.map(p => p.seatNumber).filter(Boolean);
  
  // Calculate table value from actual seated players' buy-ins
  const tableValue = seatedPlayersData.reduce((total, player) => {
    const buyIn = player.buyInAmount || player.sessionBuyInAmount || 0;
    console.log(`[TABLE VALUE] Player: ${player.playerName}, Buy-In: ${buyIn}`);
    return total + Number(buyIn);
  }, 0);
  
  console.log(`[TABLE VALUE] Total: ₹${tableValue}, Players:`, seatedPlayersData);

  // Parse table data from notes
  const noteParts = table.notes ? table.notes.split('|').map(p => p.trim()) : [];

  // Get club logo URL
  const clubLogoUrl = clubData?.logoUrl || null;
  
  const gameType = table.notes?.match(/Game Type: ([^|]+)/)?.[1]?.trim() || noteParts[1] || table.tableType;
  const stakes = table.notes?.match(/Stakes: ([^|]+)/)?.[1]?.trim() || noteParts[2]?.replace('Stakes:', '').trim() || `₹${table.minBuyIn || 0}/₹${table.maxBuyIn || 0}`;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl p-8 max-w-4xl w-full border-2 border-blue-500 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white">Table {table.tableNumber} - {gameType}</h2>
            <p className="text-gray-400">Stakes: {stakes}</p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700 mb-6">
          <button
            onClick={() => setActiveTab('view')}
            className={`px-4 py-2 font-semibold transition-all ${
              activeTab === 'view'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Live View
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-semibold transition-all ${
              activeTab === 'history'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            History
          </button>
        </div>

        {activeTab === 'view' ? (
          <>
            {/* Dealer Info (if assigned) */}
            {dealerInfo && (
              <div className="mb-4 flex items-center justify-center gap-3 bg-purple-900/30 border border-purple-600/50 rounded-lg p-3">
                <span className="text-purple-300 font-semibold">Dealer: {dealerInfo.name}</span>
                <button
                  onClick={handleUnassignDealer}
                  disabled={unassignDealerMutation.isLoading}
                  className="hover:bg-purple-700 rounded-full p-0.5 transition-colors disabled:opacity-50"
                  title="Unassign Dealer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

        {/* Table Value Display Above Hologram */}
        <div className="mb-6 flex justify-center">
          <div className="bg-gradient-to-r from-yellow-600 to-orange-600 px-6 py-3 rounded-2xl shadow-xl border-2 border-yellow-400">
            <div className="text-xs text-yellow-100 text-center mb-1">TABLE VALUE</div>
            <div className="text-3xl font-bold text-white text-center">₹{tableValue.toLocaleString()}</div>
          </div>
        </div>

        {/* Poker Table Visualization - Standard Oval Shape */}
        <div className="relative w-full" style={{ aspectRatio: '1.8 / 1' }}>
          {/* Outer Rail - Dark Wood */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900 via-yellow-900 to-amber-950 shadow-2xl" style={{ borderRadius: '50%' }}>
            {/* Padding Rail - Grey Cushion */}
            <div className="absolute inset-[10px] bg-gradient-to-br from-slate-600 via-slate-500 to-slate-600" style={{ borderRadius: '50%' }}>
              {/* Green Felt Surface */}
              <div className="absolute inset-[8px] bg-gradient-to-br from-emerald-700 via-green-800 to-emerald-900 shadow-inner" style={{ borderRadius: '50%' }}>
                {/* Subtle felt ring */}
                <div className="absolute inset-[30px] border border-emerald-600/30" style={{ borderRadius: '50%' }}></div>
              </div>
            </div>
          </div>

          {/* Center Logo - Club Logo */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-24 h-24 bg-white/15 rounded-full border-2 border-white/25 flex items-center justify-center shadow-xl backdrop-blur-sm overflow-hidden">
              {clubLogoUrl ? (
                <>
                  <img
                    src={clubLogoUrl}
                    alt="Club Logo"
                    className="w-full h-full object-contain rounded-full p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.logo-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="logo-fallback text-3xl font-bold text-white/40 hidden items-center justify-center w-full h-full">♠</div>
                </>
              ) : clubFetched ? (
                // Only show the spade fallback once we've actually confirmed
                // no logo exists — otherwise the modal flashes the spade for a
                // frame before the fetch resolves.
                <div className="text-3xl font-bold text-white/40">♠</div>
              ) : null}
            </div>
          </div>

          {/* Dealer Position */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-20">
            {dealerInfo && (
              <div className="mb-2 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                <span>👤 {dealerInfo.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnassignDealer();
                  }}
                  disabled={unassignDealerMutation.isLoading}
                  className="hover:bg-purple-700 rounded-full p-0.5 transition-colors disabled:opacity-50"
                  title="Unassign Dealer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <div className="bg-yellow-600 text-white px-3 py-1 rounded-full text-sm font-bold">
              D
            </div>
          </div>

          {/* Seats arranged around oval */}
          {seats.map((seatNum) => {
            const angle = (360 / table.maxSeats) * (seatNum - 1);
            const radians = (angle - 90) * (Math.PI / 180);
            const x = 50 + 42 * Math.cos(radians);
            const y = 50 + 40 * Math.sin(radians);
            const isOccupied = occupiedSeats.includes(seatNum);
            const seatedPlayer = seatedPlayersData.find(p => p.seatNumber === seatNum);

            return (
              <div
                key={seatNum}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-lg transition-all cursor-pointer ${
                    isOccupied
                      ? 'bg-blue-600 text-white border-2 border-blue-400 shadow-lg shadow-blue-500/30'
                      : 'bg-slate-700 text-gray-400 border-2 border-slate-600 hover:border-blue-500'
                  }`}
                  title={seatedPlayer ? seatedPlayer.playerName : `Seat ${seatNum}`}
                >
                  {isOccupied && seatedPlayer ? (
                    seatedPlayer.playerName?.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase()
                  ) : (
                    seatNum
                  )}
                </div>
                <div className="text-center text-xs text-white mt-1 whitespace-nowrap max-w-[80px] overflow-hidden text-ellipsis">
                  {isOccupied && seatedPlayer ? seatedPlayer.playerName : `Seat ${seatNum}`}
                </div>
                {isOccupied && seatedPlayer && (() => {
                  const creditTbl = Math.max(0, Number(seatedPlayer.creditOnTableThisSession) || 0);
                  const cashTbl = Math.max(0, Number(seatedPlayer.cashOnTableThisSession) || 0);
                  const totalTbl = Math.max(0, Number(seatedPlayer.buyInAmount) || cashTbl + creditTbl);
                  return (
                    <div className="text-left text-[9px] leading-snug text-slate-100 bg-slate-900/95 px-2 py-1 rounded mt-0.5 w-[92px] tabular-nums">
                      <div className="text-emerald-200">Cash ₹{cashTbl.toLocaleString()}</div>
                      <div className="text-amber-200">Credit ₹{creditTbl.toLocaleString()}</div>
                      <div className="text-yellow-300 font-semibold border-t border-white/15 mt-0.5 pt-0.5">
                        Total ₹{totalTbl.toLocaleString()}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Table Info */}
        <div className="mt-6 grid grid-cols-4 gap-4 bg-slate-800 p-4 rounded-lg">
          <div className="text-center">
            <div className="text-gray-400 text-sm">Players</div>
            <div className="text-white font-bold text-xl">{table.currentSeats || 0}/{table.maxSeats}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Table Value</div>
            <div className="text-yellow-400 font-bold text-xl">₹{tableValue.toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Buy-In Range</div>
            <div className="text-white font-bold text-lg">₹{(table.minBuyIn || 0).toLocaleString()} - ₹{(table.maxBuyIn || 0).toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm">Status</div>
            <div
              className={`font-bold text-xl ${
                isStaffSessionPausedNotes(table.notes)
                  ? 'text-yellow-400'
                  : table.status === 'OCCUPIED' || (table.currentSeats || 0) > 0
                    ? 'text-green-400'
                    : table.status === 'AVAILABLE'
                      ? 'text-yellow-400'
                      : 'text-gray-400'
              }`}
            >
              {isStaffSessionPausedNotes(table.notes)
                ? 'Paused'
                : table.status === 'OCCUPIED' || (table.currentSeats || 0) > 0
                  ? 'Active'
                  : table.status === 'AVAILABLE'
                    ? 'Waiting'
                    : table.status}
            </div>
          </div>
        </div>

        {/* Session timer whenever staff session clock exists in notes */}
        {hasStaffTableSessionStarted(table.notes) && (
          <div className="mt-4 bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-lg text-center">
            <div className="text-blue-100 text-sm mb-1">
              {isStaffSessionPausedNotes(table.notes) ? 'SESSION PAUSED (TIME)' : 'SESSION RUNNING TIME'}
            </div>
            <div className="text-white font-bold text-3xl font-mono">{sessionTime}</div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-lg font-bold text-white transition-colors"
          >
            Close
          </button>
        </div>
          </>
        ) : (
          /* History Tab */
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white mb-4">Buy-In & Buy-Out History</h3>
            {tableHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No transaction history for this table yet.
              </div>
            ) : (
              <div className="space-y-3">
                {tableHistory.map((transaction, idx) => {
                  const isBuyIn = ['Table Buy In', 'Buy In', 'Club Buy In', 'Credit'].includes(transaction.type);
                  const isBuyOut = ['Table Buy Out', 'Debit'].includes(transaction.type) || 
                                   (transaction.type === 'Deposit' && transaction.notes?.includes('buy-out'));
                  
                  return (
                    <div 
                      key={transaction.id || idx} 
                      className={`p-4 rounded-lg border-2 ${
                        isBuyIn 
                          ? 'bg-green-900/20 border-green-600/50' 
                          : 'bg-orange-900/20 border-orange-600/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className={`text-2xl ${isBuyIn ? '💰' : '💵'}`}>
                              {isBuyIn ? '💰' : '💵'}
                            </span>
                            <div>
                              <div className="font-bold text-white">
                                {transaction.playerName || transaction.player_name || 'Unknown Player'}
                              </div>
                              <div className="text-sm text-gray-400">
                                {isBuyIn ? 'Buy-In' : 'Buy-Out'}
                                {transaction.notes &&
                                  ` • ${sanitizeTransactionNotesForDisplay(transaction.notes)}`}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(transaction.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={`text-right ${
                          isBuyIn ? 'text-green-400' : 'text-orange-400'
                        }`}>
                          <div className="font-bold text-2xl">
                            {isBuyIn ? '+' : ''}₹{Number(transaction.amount).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {transaction.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-lg font-bold text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Table Management View Component (CRUD)
// ============================================================================
function TableManagementView({ selectedClubId, tables, tablesLoading, canManageTables = true }) {
  const [subTab, setSubTab] = useState("all");
  const [selectedTableForDealer, setSelectedTableForDealer] = useState("");
  const [selectedDealer, setSelectedDealer] = useState("");
  
  // Filter: Only non-Rummy (poker) tables — case-insensitive so "RUMMY"/"Rummy" never slip in
  const regularTables = tables.filter(t => String(t.tableType || '').toUpperCase() !== 'RUMMY');
  
  const [tableForm, setTableForm] = useState({
    tableName: "",
    tableNumber: "",
    gameType: "Texas Hold'em",
    customGameType: "",
    maxSeats: 8,
    stakes: "",
    minPlayTime: "30",
    callTime: 5,
    cashOutWindow: 10,
    sessionTimeout: 120,
    tableType: "CASH",
    minBuyIn: "",
    maxBuyIn: "",
    notes: "",
  });
  const [editingTable, setEditingTable] = useState(null);
  const queryClient = useQueryClient();
  const [minPlayTimePreset, setMinPlayTimePreset] = useState("30");

  // Fetch dealers (staff with DEALER role)
  const { data: staffData, isLoading: dealersLoading } = useQuery({
    queryKey: ['staff', selectedClubId],
    queryFn: () => staffAPI.getStaff(selectedClubId),
    enabled: !!selectedClubId,
  });

  // Filter to get all active dealers (role is 'Dealer' not 'DEALER')
  const allDealers = staffData?.filter(staff => staff.role === 'Dealer' && staff.status === 'Active') || [];

  // Get dealers on leave for today
  const today = new Date().toISOString().split('T')[0];
  const { data: dealersOnLeaveData } = useQuery({
    queryKey: ['dealersOnLeave', selectedClubId, today],
    queryFn: () => shiftsAPI.getDealers(selectedClubId, today),
    enabled: !!selectedClubId,
  });

  // Get dealers with shifts today (for assign dealer functionality)
  const { data: availableDealersForToday } = useQuery({
    queryKey: ['availableDealersForToday', selectedClubId, today],
    queryFn: () => shiftsAPI.getAvailableDealersForDate(selectedClubId, today),
    enabled: !!selectedClubId,
  });

  // Dealers on leave today (those NOT in the filtered list from API)
  // The API returns dealers NOT on leave, so dealers missing from that list are on leave
  const dealersNotOnLeave = dealersOnLeaveData?.dealers || [];
  const dealersOnLeaveIds = new Set(
    allDealers
      .filter(dealer => !dealersNotOnLeave.find(d => d.id === dealer.id))
      .map(d => d.id)
  );

  // Get dealers that are already assigned to active tables (check all tables, not just regular)
  const assignedDealerIds = new Set(
    tables
      .filter(table => (table.status === 'OCCUPIED' || table.status === 'RESERVED') && table.notes)
      .map(table => {
        // Extract dealer ID from notes (format: "Dealer: dealerId")
        const dealerMatch = table.notes?.match(/Dealer:\s*([a-f0-9-]{36})/i);
        return dealerMatch ? dealerMatch[1] : null;
      })
      .filter(Boolean)
  );

  // For assign dealer: Only show dealers who have shifts today AND are not on leave
  // Also filter to only poker dealers (gameType is 'poker' or not set)
  const dealersForAssignment = (availableDealersForToday?.dealers || []).filter(
    dealer => !assignedDealerIds.has(dealer.id) && dealer.gameType !== 'rummy'
  );

  // For general display: Filter out dealers on leave, only show poker dealers
  const availableDealers = allDealers.filter(
    dealer => !assignedDealerIds.has(dealer.id) && !dealersOnLeaveIds.has(dealer.id) && dealer.gameType !== 'rummy'
  );

  // Assign dealer mutation
  const assignDealerMutation = useMutation({
    mutationFn: async ({ tableId, dealerId }) => {
      const table = regularTables.find(t => t.id === tableId);
      const dealer = allDealers.find(d => d.id === dealerId);
      
      if (!table || !dealer) {
        throw new Error('Table or dealer not found');
      }

      // Update table notes to include dealer info
      const existingNotes = table.notes || '';
      const notesWithoutDealer = existingNotes.replace(/\s*\|\s*Dealer:.*$/i, '');
      const updatedNotes = `${notesWithoutDealer} | Dealer: ${dealer.id} (${dealer.name})`.trim();

      return tablesAPI.updateTable(selectedClubId, tableId, {
        notes: updatedNotes
      });
    },
    onSuccess: () => {
      toast.success('Dealer assigned successfully');
      setSelectedTableForDealer("");
      setSelectedDealer("");
      queryClient.invalidateQueries(['tables', selectedClubId]);
      queryClient.invalidateQueries(['staff', selectedClubId]);
      queryClient.invalidateQueries(['all-tables', selectedClubId]);
      queryClient.invalidateQueries(['rummy-tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign dealer');
    },
  });

  const handleAssignDealer = () => {
    if (!selectedTableForDealer || !selectedDealer) {
      toast.error('Please select both table and dealer');
      return;
    }
    assignDealerMutation.mutate({
      tableId: selectedTableForDealer,
      dealerId: selectedDealer,
    });
  };

  // Create table mutation
  const createTableMutation = useMutation({
    mutationFn: (data) => tablesAPI.createTable(selectedClubId, data),
    onSuccess: () => {
      toast.success('Table created successfully');
      setTableForm({
        tableName: "",
        tableNumber: "",
        gameType: "Texas Hold'em",
        customGameType: "",
        maxSeats: 8,
        stakes: "",
        minPlayTime: "30",
        callTime: 5,
        cashOutWindow: 10,
        sessionTimeout: 120,
        tableType: "CASH",
        minBuyIn: "",
        maxBuyIn: "",
        notes: "",
      });
      setMinPlayTimePreset("30");
      setSubTab("all");
      queryClient.invalidateQueries(['tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create table');
    },
  });

  // Update table mutation
  const updateTableMutation = useMutation({
    mutationFn: ({ tableId, data }) => tablesAPI.updateTable(selectedClubId, tableId, data),
    onSuccess: () => {
      toast.success('Table updated successfully');
      setEditingTable(null);
      setTableForm({
        tableName: "",
        tableNumber: "",
        gameType: "Texas Hold'em",
        customGameType: "",
        maxSeats: 8,
        stakes: "",
        minPlayTime: "30",
        callTime: 5,
        cashOutWindow: 10,
        sessionTimeout: 120,
        tableType: "CASH",
        minBuyIn: "",
        maxBuyIn: "",
        notes: "",
      });
      setMinPlayTimePreset("30");
      setSubTab("all");
      queryClient.invalidateQueries(['tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update table');
    },
  });

  // Delete table mutation
  const deleteTableMutation = useMutation({
    mutationFn: (tableId) => tablesAPI.deleteTable(selectedClubId, tableId),
    onSuccess: () => {
      toast.success('Table deleted successfully');
      queryClient.invalidateQueries(['tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete table');
    },
  });

  // Start session mutation
  const startSessionMutation = useMutation({
    mutationFn: (tableId) => tablesAPI.resumeSession(selectedClubId, tableId),
    onSuccess: () => {
      toast.success('Session started successfully');
      queryClient.invalidateQueries(['tables', selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start session');
    },
  });

  const handleCreateTable = (e) => {
    e.preventDefault();
    if (!selectedClubId) {
      toast.error('Please select a club first');
      return;
    }

    if (!tableForm.tableNumber) {
      toast.error('Table number is required');
      return;
    }

    const normalizedMinPlayTime = minPlayTimePreset === "custom"
      ? Math.max(30, parseInt(tableForm.minPlayTime) || 30)
      : Math.max(30, parseInt(minPlayTimePreset) || 30);

    createTableMutation.mutate({
      tableNumber: parseInt(tableForm.tableNumber),
      tableType: tableForm.tableType,
      maxSeats: parseInt(tableForm.maxSeats),
      minBuyIn: tableForm.minBuyIn ? parseFloat(tableForm.minBuyIn) : null,
      maxBuyIn: tableForm.maxBuyIn ? parseFloat(tableForm.maxBuyIn) : null,
      notes: tableForm.notes || `${tableForm.tableName || 'Table ' + tableForm.tableNumber} | ${tableForm.gameType === 'Custom' ? tableForm.customGameType : tableForm.gameType} | Stakes: ${tableForm.stakes} | Min Play: ${normalizedMinPlayTime}m | Call: ${tableForm.callTime}m | Cash-out: ${tableForm.cashOutWindow}m | Timeout: ${tableForm.sessionTimeout}m`,
    });
  };

  const handleUpdateTable = (e) => {
    e.preventDefault();
    if (!editingTable) return;

    const normalizedMinPlayTime = minPlayTimePreset === "custom"
      ? Math.max(30, parseInt(tableForm.minPlayTime) || 30)
      : Math.max(30, parseInt(minPlayTimePreset) || 30);

    updateTableMutation.mutate({
      tableId: editingTable.id,
      data: {
        // tableNumber cannot be updated (removed to fix 400 error)
        tableType: tableForm.tableType,
        maxSeats: parseInt(tableForm.maxSeats),
        minBuyIn: tableForm.minBuyIn ? parseFloat(tableForm.minBuyIn) : null,
        maxBuyIn: tableForm.maxBuyIn ? parseFloat(tableForm.maxBuyIn) : null,
        notes: tableForm.notes || `${tableForm.tableName || 'Table ' + tableForm.tableNumber} | ${tableForm.gameType === 'Custom' ? tableForm.customGameType : tableForm.gameType} | Stakes: ${tableForm.stakes} | Min Play: ${normalizedMinPlayTime}m | Call: ${tableForm.callTime}m | Cash-out: ${tableForm.cashOutWindow}m | Timeout: ${tableForm.sessionTimeout}m`,
      },
    });
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    
    // Parse notes to extract form values if available
    const noteParts = table.notes ? table.notes.split('|').map(p => p.trim()) : [];
    const tableName = noteParts[0] || `Table ${table.tableNumber}`;
    const gameType = noteParts[1] || 'Texas Hold\'em';
    const stakes = noteParts[2]?.replace('Stakes:', '').trim() || '';
    const minPlayTime = noteParts[3]?.match(/\d+/)?.[0] || '30';
    const callTime = noteParts[4]?.match(/\d+/)?.[0] || '5';
    const cashOutWindow = noteParts[5]?.match(/\d+/)?.[0] || '10';
    const sessionTimeout = noteParts[6]?.match(/\d+/)?.[0] || '120';
    
    setTableForm({
      tableName,
      tableNumber: table.tableNumber?.toString() || "",
      gameType,
      customGameType: "",
      maxSeats: table.maxSeats || 8,
      stakes,
      minPlayTime,
      callTime: parseInt(callTime),
      cashOutWindow: parseInt(cashOutWindow),
      sessionTimeout: parseInt(sessionTimeout),
      tableType: table.tableType || "CASH",
      minBuyIn: table.minBuyIn?.toString() || "",
      maxBuyIn: table.maxBuyIn?.toString() || "",
      notes: table.notes || "",
    });
    
    const presets = new Set(["30", "45", "60", "90", "120", "150", "180"]);
    setMinPlayTimePreset(presets.has(minPlayTime) ? minPlayTime : "custom");
    setSubTab("add");
  };

  const handleDeleteTable = (table) => {
    if (window.confirm(`Are you sure you want to delete Table ${table.tableNumber}?`)) {
      deleteTableMutation.mutate(table.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold mb-6">Table Management</h2>

        {/* Sub Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-4 mb-6">
          <button
            onClick={() => setSubTab("all")}
            className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
              subTab === "all"
                ? "bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-lg"
                : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}
          >
            All Tables
          </button>
          {canManageTables && (
            <button
              onClick={() => {
                setSubTab("add");
                setEditingTable(null);
                setTableForm({
                  tableName: "",
                  tableNumber: "",
                  gameType: "Texas Hold'em",
                  customGameType: "",
                  maxSeats: 8,
                  stakes: "",
                  minPlayTime: "30",
                  callTime: 5,
                  cashOutWindow: 10,
                  sessionTimeout: 120,
                  tableType: "CASH",
                  minBuyIn: "",
                  maxBuyIn: "",
                  notes: "",
                });
                setMinPlayTimePreset("30");
              }}
              className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
                subTab === "add"
                  ? "bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-lg"
                  : "bg-slate-700 text-gray-400 hover:bg-slate-600"
              }`}
            >
              Add New Table
            </button>
          )}
        </div>

        {/* All Tables List */}
        {subTab === "all" && (
          <div className="space-y-4">
            {tablesLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                <p>Loading tables...</p>
              </div>
            ) : regularTables.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎲</div>
                <p className="text-xl text-gray-300">No regular tables created yet</p>
                <p className="text-gray-400 text-sm mt-2">Click "Add New Table" to create your first table (Rummy tables are managed separately)</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {regularTables.map((table) => {
                  const notes = String(table.notes || '');
                  const hasSessionMarker =
                    /Session Started:/i.test(notes) ||
                    /Paused Elapsed:/i.test(notes);
                  const sessionRunning =
                    hasSessionMarker ||
                    (Number(table.currentSeats || 0) > 0) ||
                    ['OCCUPIED', 'RESERVED', 'PAUSED'].includes(String(table.status || '').toUpperCase());

                  return (
                  <div
                    key={table.id}
                    className="bg-slate-700 rounded-xl p-5 border border-slate-600"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-white">Table {table.tableNumber}</h3>
                        <p className="text-sm text-gray-400">{table.tableType}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        table.status === 'AVAILABLE' ? 'bg-green-600/20 text-green-400' :
                        table.status === 'OCCUPIED' ? 'bg-blue-600/20 text-blue-400' :
                        table.status === 'RESERVED' ? 'bg-yellow-600/20 text-yellow-400' :
                        'bg-gray-600/20 text-gray-400'
                      }`}>
                        {table.status}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Max Seats:</span>
                        <span className="text-white font-medium">{table.maxSeats}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Stakes:</span>
                        <span className="text-white font-medium">₹{table.minBuyIn || 0}/₹{table.maxBuyIn || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Current Seats:</span>
                        <span className="text-white font-medium">{table.currentSeats || 0} / {table.maxSeats}</span>
                      </div>
                    </div>

                    {!sessionRunning && canManageTables ? (
                      <div className="flex gap-2">
                        {table.status === 'AVAILABLE' || table.status === 'CLOSED' ? (
                          <button
                            onClick={() => startSessionMutation.mutate(table.id)}
                            disabled={startSessionMutation.isLoading}
                            className="flex-1 bg-green-600 hover:bg-green-500 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                          >
                            <span>▶</span>
                            <span>Start Session</span>
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleEditTable(table)}
                          className="flex-1 bg-blue-600 hover:bg-blue-500 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTable(table)}
                          disabled={deleteTableMutation.isLoading}
                          className="flex-1 bg-red-600 hover:bg-red-500 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        {sessionRunning
                          ? 'Session running - manage this table from Live Tables / Session Control.'
                          : 'View-only access for this role.'}
                      </div>
                    )}
                  </div>
                )})}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Table Form */}
        {subTab === "add" && canManageTables && (
          <div className="bg-slate-700 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-6">{editingTable ? 'Edit Table' : 'Create New Table'}</h3>
            <form onSubmit={editingTable ? handleUpdateTable : handleCreateTable} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Table Name</label>
                <input
                  type="text"
                  value={tableForm.tableName}
                  onChange={(e) => setTableForm({ ...tableForm, tableName: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  placeholder="Table 1"
                  disabled={!selectedClubId}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Game Type</label>
                <select
                  value={tableForm.gameType}
                  onChange={(e) => setTableForm({ ...tableForm, gameType: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={!selectedClubId}
                >
                  <option value="Texas Hold'em">Texas Hold'em</option>
                  <option value="Omaha">Omaha</option>
                  <option value="Seven Card Stud">Seven Card Stud</option>
                  <option value="Razz">Razz</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              {tableForm.gameType === 'Custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Custom Game Type</label>
                  <input
                    type="text"
                    value={tableForm.customGameType}
                    onChange={(e) => setTableForm({ ...tableForm, customGameType: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                    placeholder="Enter custom game type"
                    disabled={!selectedClubId}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Table Number *</label>
                <input
                  type="number"
                  value={tableForm.tableNumber}
                  onChange={(e) => setTableForm({ ...tableForm, tableNumber: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  required
                  disabled={!selectedClubId}
                  min="1"
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Max Players</label>
                <input
                  type="number"
                  value={tableForm.maxSeats}
                  onChange={(e) => setTableForm({ ...tableForm, maxSeats: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={!selectedClubId}
                  min="2"
                  max="10"
                  placeholder="8"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Blind Levels / Stakes</label>
                <input
                  type="text"
                  value={tableForm.stakes}
                  onChange={(e) => setTableForm({ ...tableForm, stakes: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  placeholder="₹25/₹50"
                  disabled={!selectedClubId}
                />
              </div>

              {/* Session Parameters */}
              <div className="border-t border-slate-600 pt-4 mt-4">
                <h4 className="text-white text-sm font-semibold mb-3">Session Parameters</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-white text-xs mb-1">Min Play Time (minutes)</label>
                    <div className="space-y-2">
                      <select
                        value={minPlayTimePreset}
                        onChange={(e) => {
                          const value = e.target.value;
                          setMinPlayTimePreset(value);
                          if (value !== "custom") {
                            setTableForm({ ...tableForm, minPlayTime: value });
                          } else if (!tableForm.minPlayTime || parseInt(tableForm.minPlayTime) < 30) {
                            setTableForm({ ...tableForm, minPlayTime: "30" });
                          }
                        }}
                        className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                        disabled={!selectedClubId}
                      >
                        <option value="30">30 minutes</option>
                        <option value="45">45 minutes</option>
                        <option value="60">60 minutes</option>
                        <option value="90">90 minutes</option>
                        <option value="120">120 minutes</option>
                        <option value="150">150 minutes</option>
                        <option value="180">180 minutes</option>
                        <option value="custom">Custom...</option>
                      </select>
                      {minPlayTimePreset === "custom" && (
                        <input
                          type="number"
                          min={30}
                          value={tableForm.minPlayTime}
                          onChange={(e) => setTableForm({ ...tableForm, minPlayTime: e.target.value })}
                          className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                          placeholder="Enter minutes (min 30)"
                          disabled={!selectedClubId}
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-xs mb-1">Call Time (minutes)</label>
                    <input
                      type="number"
                      value={tableForm.callTime}
                      onChange={(e) => setTableForm({ ...tableForm, callTime: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                      placeholder="5"
                      disabled={!selectedClubId}
                    />
                  </div>

                  <div>
                    <label className="block text-white text-xs mb-1">Cash-out Window (minutes)</label>
                    <input
                      type="number"
                      value={tableForm.cashOutWindow}
                      onChange={(e) => setTableForm({ ...tableForm, cashOutWindow: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                      placeholder="10"
                      disabled={!selectedClubId}
                    />
                  </div>

                  <div>
                    <label className="block text-white text-xs mb-1">Session Timeout (minutes)</label>
                    <input
                      type="number"
                      value={tableForm.sessionTimeout}
                      onChange={(e) => setTableForm({ ...tableForm, sessionTimeout: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                      placeholder="120"
                      disabled={!selectedClubId}
                    />
                  </div>
                </div>
              </div>

              {/* Buy-in Range for backend API */}
              <div className="grid grid-cols-2 gap-4 border-t border-slate-600 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Min Buy-In (₹)</label>
                  <input
                    type="number"
                    value={tableForm.minBuyIn}
                    onChange={(e) => setTableForm({ ...tableForm, minBuyIn: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                    disabled={!selectedClubId}
                    min="0"
                    step="0.01"
                    placeholder="1000.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Max Buy-In (₹)</label>
                  <input
                    type="number"
                    value={tableForm.maxBuyIn}
                    onChange={(e) => setTableForm({ ...tableForm, maxBuyIn: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                    disabled={!selectedClubId}
                    min="0"
                    step="0.01"
                    placeholder="10000.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Table Type (Backend)</label>
                <select
                  value={tableForm.tableType}
                  onChange={(e) => setTableForm({ ...tableForm, tableType: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={!selectedClubId}
                >
                  <option value="CASH">Cash Game</option>
                  <option value="TOURNAMENT">Tournament</option>
                  <option value="HIGH_STAKES">High Stakes</option>
                  <option value="PRIVATE">Private</option>
                </select>
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  type="submit"
                  disabled={createTableMutation.isLoading || updateTableMutation.isLoading || !selectedClubId}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {createTableMutation.isLoading || updateTableMutation.isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {editingTable ? 'Updating...' : 'Creating...'}
                    </span>
                  ) : (
                    editingTable ? 'Update Table' : 'Create Table'
                  )}
                </button>
                {editingTable && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTable(null);
                      setTableForm({
                        tableName: "",
                        tableNumber: "",
                        gameType: "Texas Hold'em",
                        customGameType: "",
                        maxSeats: 8,
                        stakes: "",
                        minPlayTime: "30",
                        callTime: 5,
                        cashOutWindow: 10,
                        sessionTimeout: 120,
                        tableType: "CASH",
                        minBuyIn: "",
                        maxBuyIn: "",
                        notes: "",
                      });
                      setMinPlayTimePreset("30");
                      setSubTab("all");
                    }}
                    className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 rounded-lg font-semibold transition-all"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Dealer Assignment Section */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-xl font-bold mb-4">Dealer Assignment</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Select Table</label>
            <select 
              value={selectedTableForDealer}
              onChange={(e) => setSelectedTableForDealer(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              disabled={!selectedClubId}
            >
              <option value="">-- Select Table --</option>
              {regularTables.map((table) => (
                <option key={table.id} value={table.id}>
                  Table {table.tableNumber} - {table.tableType} ({table.status})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Assign Dealer</label>
            <select 
              value={selectedDealer}
              onChange={(e) => setSelectedDealer(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              disabled={!selectedClubId || dealersLoading}
            >
              <option value="">-- Select Dealer --</option>
              {dealersLoading ? (
                <option disabled>Loading dealers...</option>
              ) : dealersForAssignment.length === 0 ? (
                <option disabled>
                  {allDealers.length > 0 
                    ? 'No dealers available (all assigned, on leave, or no shift today)' 
                    : 'No active dealers found'}
                </option>
              ) : (
                dealersForAssignment.map((dealer) => (
                  <option key={dealer.id} value={dealer.id}>
                    {dealer.name} ({dealer.employeeId || 'No ID'})
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        <button 
          onClick={handleAssignDealer}
          disabled={!selectedTableForDealer || !selectedDealer || assignDealerMutation.isLoading || !selectedClubId}
          className="mt-4 bg-purple-600 hover:bg-purple-500 px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {assignDealerMutation.isLoading ? 'Assigning...' : 'Assign Dealer'}
        </button>
        {allDealers.length === 0 && !dealersLoading && selectedClubId && (
          <p className="mt-2 text-sm text-yellow-400">
            ⚠️ No active dealers found. Please create dealer staff members first.
          </p>
        )}
        {availableDealers.length === 0 && allDealers.length > 0 && !dealersLoading && (
          <p className="mt-2 text-sm text-amber-400">
            ℹ️ All dealers are currently assigned to active tables. Free up a dealer by ending a table session.
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Table Buy-In View Component (Waitlist Management)
// ============================================================================
function TableBuyInView({ selectedClubId, tables, waitlist, waitlistLoading, canAssignSeat = true }) {
  const queryClient = useQueryClient();
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedSeat, setSelectedSeat] = useState("");
  const [selectedTableForView, setSelectedTableForView] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFullCurrentWaitlist, setShowFullCurrentWaitlist] = useState(false);
  const [showFullReorderWaitlist, setShowFullReorderWaitlist] = useState(false);
  const itemsPerPage = 10;
  // Local poker tables list for this view (non-RUMMY, case-insensitive)
  const regularTables = (tables || []).filter(
    (t) => String(t.tableType || "").toUpperCase() !== "RUMMY"
  );

  const selectedTableViewId = selectedTableForView?.table?.id || '';
  const { data: seatedPlayersForSelectedTable = [] } = useQuery({
    queryKey: ['seatedPlayersForTable', selectedClubId, selectedTableViewId, 'buyin-modal'],
    queryFn: async () => {
      if (!selectedClubId || !selectedTableViewId) return [];
      const response = await tablesAPI.getSeatedPlayersForTable(selectedClubId, selectedTableViewId);
      return response?.seatedPlayers || [];
    },
    enabled: !!selectedClubId && !!selectedTableViewId,
  });

  const occupiedSeatMapForView = new Map(
    (Array.isArray(seatedPlayersForSelectedTable) ? seatedPlayersForSelectedTable : [])
      .map((p) => [Number(p?.seatNumber), p?.playerName || p?.name || 'Occupied'])
      .filter(([seat]) => Number.isFinite(seat) && seat > 0)
  );

  // Remove waitlist entry mutation
  const removeWaitlistMutation = useMutation({
    mutationFn: (entryId) => waitlistAPI.deleteWaitlistEntry(selectedClubId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries(['waitlist', selectedClubId]);
      toast.success('Player removed from waitlist');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove from waitlist');
    }
  });

  // Assign seat mutation
  const assignSeatMutation = useMutation({
    mutationFn: ({ entryId, tableId, seatedBy, seatNumber }) => 
      waitlistAPI.assignSeat(selectedClubId, entryId, { tableId, seatedBy: seatedBy || 'Super Admin', seatNumber: seatNumber != null ? parseInt(seatNumber, 10) : undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries(['waitlist', selectedClubId]);
      queryClient.invalidateQueries(['tables', selectedClubId]);
      setSelectedPlayer("");
      setSelectedTable("");
      setSelectedSeat("");
      toast.success('Seat assigned successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to assign seat');
    }
  });

  // Reorder waitlist mutation
  const reorderWaitlistMutation = useMutation({
    mutationFn: ({ entryId, newPriority }) => 
      waitlistAPI.updateWaitlistEntry(selectedClubId, entryId, { priority: newPriority }),
    onSuccess: () => {
      queryClient.invalidateQueries(['waitlist', selectedClubId]);
      toast.success('Waitlist reordered');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reorder waitlist');
    }
  });

  const handleAssignSeat = () => {
    if (!selectedPlayer || !selectedTable || !selectedSeat) {
      toast.error('Please select player, table, and seat');
      return;
    }
    assignSeatMutation.mutate({
      entryId: selectedPlayer,
      tableId: selectedTable,
      seatedBy: 'Super Admin',
      seatNumber: selectedSeat ? parseInt(selectedSeat, 10) : undefined
    });
  };

  const handleMoveUp = (entry, index) => {
    if (index === 0) return;
    const currentPriority = entry.priority || 0;
    reorderWaitlistMutation.mutate({ entryId: entry.id, newPriority: currentPriority + 1 });
  };

  const handleMoveDown = (entry, index) => {
    if (index === waitlist.length - 1) return;
    const currentPriority = entry.priority || 0;
    reorderWaitlistMutation.mutate({ entryId: entry.id, newPriority: currentPriority - 1 });
  };

  const handleViewTable = (entry) => {
    // Poker page: only consider poker tables (non-RUMMY, case-insensitive)
    const pokerTables = tables.filter(t => String(t.tableType || '').toUpperCase() !== 'RUMMY');
    let table = pokerTables.find(t => t.tableType === entry.tableType);
    if (!table) {
      table = pokerTables.find(t => t.status === 'AVAILABLE' && t.currentSeats < t.maxSeats);
    }
    if (!table && pokerTables.length > 0) {
      table = pokerTables[0];
    }
    if (table) {
      setSelectedTableForView({ table, waitlistEntry: entry });
    } else {
      toast.error('No poker tables available. Please create a poker table first.');
    }
  };

  // Sort waitlist by position (priority DESC, then createdAt ASC)
  // Poker Table Management: only show waitlist entries for poker (not rummy; case-insensitive)
  const pendingWaitlist = waitlist
    .filter(e => String(e.status || '').toUpperCase() === 'PENDING' && String(e.requestedGameType || '').toUpperCase() !== 'RUMMY')
    .sort((a, b) => {
      // Sort by priority descending first
      const priorityA = a.priority || 0;
      const priorityB = b.priority || 0;
      if (priorityB !== priorityA) {
        return priorityB - priorityA;
      }
      // Then by createdAt ascending (oldest first)
      return new Date(a.createdAt) - new Date(b.createdAt);
    })
    .map((entry, index) => ({
      ...entry,
      position: index + 1 // Add position number based on sorted order
    }));

  // Pagination
  const totalPages = Math.ceil(pendingWaitlist.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWaitlist = pendingWaitlist.slice(startIndex, endIndex);

  // Reset to page 1 if current page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['waitlist', selectedClubId] }),
        queryClient.refetchQueries({ queryKey: ['tables', selectedClubId] }),
      ]);
      toast.success('Table Buy-In data refreshed');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Waitlist Management</h2>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={waitlistLoading || isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors"
          >
            {isRefreshing ? (
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Waitlist */}
          <div className="bg-slate-700 rounded-xl p-5 min-h-[420px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Current Waitlist</h3>
              {pendingWaitlist.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowFullCurrentWaitlist(true)}
                  className="text-xs px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500 transition-colors"
                >
                  View Full List ({pendingWaitlist.length})
                </button>
              )}
            </div>
            {waitlistLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-sm">Loading...</p>
              </div>
            ) : pendingWaitlist.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No players in waitlist
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {currentWaitlist.map((entry) => (
                  <div key={entry.id} className="bg-slate-600 p-4 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-white">{entry.playerName}</div>
                        <div className="text-sm text-gray-400">Position: #{entry.position || 'N/A'}</div>
                        <div className="text-sm text-gray-400">Table Type: {entry.tableType || 'Cash Game'}</div>
                        <div className="text-sm text-blue-400">Requested Seat: {entry.requestedSeat || 'N/A'}</div>
                      </div>
                      <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded text-xs font-semibold">
                        {entry.status}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button 
                        onClick={() => handleViewTable(entry)}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      >
                        🎯 View Table
                      </button>
                      <button 
                        onClick={() => removeWaitlistMutation.mutate(entry.id)}
                        disabled={removeWaitlistMutation.isLoading}
                        className="flex-1 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 px-3 py-1.5 rounded text-sm font-medium transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-600">
                    <div className="text-sm text-gray-400">
                      Showing {startIndex + 1}-{Math.min(endIndex, pendingWaitlist.length)} of {pendingWaitlist.length}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 disabled:cursor-not-allowed rounded text-sm transition-colors"
                      >
                        Previous
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              currentPage === page 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-slate-600 hover:bg-slate-500'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 disabled:cursor-not-allowed rounded text-sm transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Seat Allocation */}
          <div className="bg-slate-700 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4">Seat Allocation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Select Player</label>
                <select 
                  value={selectedPlayer}
                  onChange={(e) => {
                    const playerId = e.target.value;
                    setSelectedPlayer(playerId);
                    
                    // Auto-select table based on player's requested table type
                    if (playerId) {
                      const playerEntry = pendingWaitlist.find(entry => entry.id === playerId);
                      if (playerEntry) {
                        // Poker page: only poker tables (non-RUMMY, case-insensitive)
                        const pokerTables = tables.filter(t => String(t.tableType || '').toUpperCase() !== 'RUMMY');
                        let matchingTable = pokerTables.find(t => t.tableType === playerEntry.tableType);
                        if (!matchingTable) {
                          matchingTable = pokerTables.find(t => t.status === 'AVAILABLE' && t.currentSeats < t.maxSeats);
                        }
                        if (!matchingTable && pokerTables.length > 0) {
                          matchingTable = pokerTables[0];
                        }
                        setSelectedTable(matchingTable?.id || '');
                        
                        // Auto-select requested seat if available
                        if (playerEntry.requestedSeat) {
                          setSelectedSeat(String(playerEntry.requestedSeat));
                        } else {
                          setSelectedSeat('');
                        }
                      }
                    } else {
                      setSelectedTable('');
                      setSelectedSeat('');
                    }
                  }}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                >
                  <option value="">-- Select Player --</option>
                  {pendingWaitlist.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.playerName} {entry.requestedSeat ? `(wants seat #${entry.requestedSeat})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedPlayer && (() => {
                const playerEntry = pendingWaitlist.find(e => e.id === selectedPlayer);
                return playerEntry ? (
                  <div className="bg-slate-600 rounded-lg p-3 text-sm">
                    <div className="text-gray-300 mb-1">
                      <span className="text-gray-400">Requested Table Type:</span> {playerEntry.tableType || 'Any'}
                    </div>
                    {playerEntry.requestedSeat && (
                      <div className="text-blue-400">
                        <span className="text-gray-400">Requested Seat:</span> #{playerEntry.requestedSeat}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Assigned Table {selectedPlayer && <span className="text-xs text-gray-400">(Auto-selected)</span>}
                </label>
                <select 
                  value={selectedTable}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={true}
                >
                  <option value="">-- No table selected --</option>
                  {regularTables.map((table) => (
                    <option key={table.id} value={table.id}>
                      Table {table.tableNumber} ({table.currentSeats}/{table.maxSeats}) - {table.status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Seat Number {selectedPlayer && pendingWaitlist.find(e => e.id === selectedPlayer)?.requestedSeat && <span className="text-xs text-blue-400">(Pre-selected)</span>}
                </label>
                <select 
                  value={selectedSeat}
                  onChange={(e) => setSelectedSeat(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  disabled={!selectedTable}
                >
                  <option value="">-- Select Seat --</option>
                  {selectedTable && (() => {
                    const table = tables.find(t => t.id === selectedTable);
                    const maxSeats = table?.maxSeats || 10;
                    const playerEntry = pendingWaitlist.find(e => e.id === selectedPlayer);
                    return Array.from({ length: maxSeats }, (_, i) => i + 1).map((seat) => (
                      <option key={seat} value={seat}>
                        Seat {seat}
                        {playerEntry?.requestedSeat === seat ? ' ⭐ (Requested)' : ''}
                      </option>
                    ));
                  })()}
                </select>
              </div>
              {canAssignSeat && (
                <button 
                  onClick={handleAssignSeat}
                  disabled={!selectedPlayer || !selectedTable || !selectedSeat || assignSeatMutation.isLoading}
                  className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 px-4 py-3 rounded-lg font-semibold transition-colors"
                >
                  {assignSeatMutation.isLoading ? 'Assigning...' : 'Assign Seat'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Player Call & Reorder */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold mb-6">Player Call & Reorder</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Call Players */}
          <div className="bg-slate-700 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4">Call Players</h3>
            <div className="space-y-3">
              <button className="w-full bg-green-600 hover:bg-green-500 px-4 py-3 rounded-lg font-semibold transition-colors">
                Call Next Player
              </button>
              <div className="text-sm text-gray-400 text-center py-2">
                {pendingWaitlist.length} player(s) in waitlist
              </div>
            </div>
          </div>

          {/* Reorder Waitlist */}
          <div className="bg-slate-700 rounded-xl p-5 min-h-[420px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Reorder Waitlist</h3>
              {pendingWaitlist.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowFullReorderWaitlist(true)}
                  className="text-xs px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500 transition-colors"
                >
                  Expand
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {pendingWaitlist.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No players to reorder
                </div>
              ) : (
                pendingWaitlist.map((entry, index) => (
                  <div key={entry.id} className="bg-slate-600 p-3 rounded flex justify-between items-center">
                    <span className="text-white">{index + 1}. {entry.playerName}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleMoveUp(entry, index)}
                        disabled={index === 0 || reorderWaitlistMutation.isLoading}
                        className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleMoveDown(entry, index)}
                        disabled={index === pendingWaitlist.length - 1 || reorderWaitlistMutation.isLoading}
                        className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Current Waitlist Modal */}
      {showFullCurrentWaitlist && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Current Waitlist ({pendingWaitlist.length})</h2>
              <button
                type="button"
                onClick={() => setShowFullCurrentWaitlist(false)}
                className="px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500"
              >
                Close
              </button>
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {pendingWaitlist.map((entry) => (
                <div key={entry.id} className="bg-slate-700 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-white">{entry.playerName}</div>
                      <div className="text-sm text-gray-400">Position: #{entry.position || 'N/A'}</div>
                      <div className="text-sm text-gray-400">Table Type: {entry.tableType || 'Cash Game'}</div>
                      <div className="text-sm text-blue-400">Requested Seat: {entry.requestedSeat || 'N/A'}</div>
                    </div>
                    <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded text-xs font-semibold">
                      {entry.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full Reorder Waitlist Modal */}
      {showFullReorderWaitlist && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-auto border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Reorder Waitlist ({pendingWaitlist.length})</h2>
              <button
                type="button"
                onClick={() => setShowFullReorderWaitlist(false)}
                className="px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500"
              >
                Close
              </button>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {pendingWaitlist.map((entry, index) => (
                <div key={entry.id} className="bg-slate-700 p-3 rounded flex justify-between items-center">
                  <span className="text-white">{index + 1}. {entry.playerName}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleMoveUp(entry, index)}
                      disabled={index === 0 || reorderWaitlistMutation.isLoading}
                      className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveDown(entry, index)}
                      disabled={index === pendingWaitlist.length - 1 || reorderWaitlistMutation.isLoading}
                      className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-2 py-1 rounded text-xs transition-colors"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table View & Seat Assignment Modal */}
      {selectedTableForView && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                Assign Player to Table {selectedTableForView.table.tableNumber}
              </h2>
              <button 
                onClick={() => setSelectedTableForView(null)}
                className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded transition-colors"
              >
                Close
              </button>
            </div>
            
            {/* Table Info */}
            <div className="bg-slate-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-lg mb-2">Table Information</h3>
              <div className="grid grid-cols-2 gap-4 text-gray-300">
                <div>
                  <span className="text-gray-400">Type:</span> {selectedTableForView.table.tableType}
                </div>
                <div>
                  <span className="text-gray-400">Seats:</span> {selectedTableForView.table.currentSeats}/{selectedTableForView.table.maxSeats}
                </div>
                <div>
                  <span className="text-gray-400">Status:</span> 
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                    selectedTableForView.table.status === 'AVAILABLE' ? 'bg-green-600' :
                    selectedTableForView.table.status === 'OCCUPIED' ? 'bg-yellow-600' :
                    'bg-gray-600'
                  }`}>
                    {selectedTableForView.table.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Available Seats:</span> {selectedTableForView.table.maxSeats - selectedTableForView.table.currentSeats}
                </div>
              </div>
            </div>

            {/* Player Info */}
            <div className="bg-slate-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-lg mb-2">Player Waiting</h3>
              <div className="grid grid-cols-2 gap-4 text-gray-300">
                <div>
                  <span className="text-gray-400">Name:</span> {selectedTableForView.waitlistEntry.playerName}
                </div>
                <div>
                  <span className="text-gray-400">Position:</span> #{selectedTableForView.waitlistEntry.position || 'N/A'}
                </div>
                <div>
                  <span className="text-gray-400">Requested Seat:</span> {selectedTableForView.waitlistEntry.requestedSeat || 'Any'}
                </div>
                <div>
                  <span className="text-gray-400">Party Size:</span> {selectedTableForView.waitlistEntry.partySize || 1}
                </div>
              </div>
            </div>

            {/* Seat Assignment Form */}
            <div className="bg-slate-700 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-4">Assign Seat</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Select Seat Number
                  </label>
                  <select
                    value={selectedSeat}
                    onChange={(e) => setSelectedSeat(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white"
                  >
                    <option value="">-- Choose a seat --</option>
                    {Array.from({ length: selectedTableForView.table.maxSeats }, (_, i) => i + 1).map(seatNum => (
                      <option
                        key={seatNum}
                        value={seatNum}
                        disabled={occupiedSeatMapForView.has(seatNum)}
                      >
                        Seat {seatNum}
                        {selectedTableForView.waitlistEntry.requestedSeat === seatNum ? ' (Requested)' : ''}
                        {occupiedSeatMapForView.has(seatNum)
                          ? ` - Occupied (${occupiedSeatMapForView.get(seatNum)})`
                          : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                {canAssignSeat ? (
                  <button
                    onClick={() => {
                      if (!selectedSeat) {
                        toast.error('Please select a seat number');
                        return;
                      }
                      const seatNum = Number(selectedSeat);
                      if (occupiedSeatMapForView.has(seatNum)) {
                        toast.error(`Seat ${seatNum} is occupied by ${occupiedSeatMapForView.get(seatNum)}`);
                        return;
                      }
                      assignSeatMutation.mutate({
                        entryId: selectedTableForView.waitlistEntry.id,
                        tableId: selectedTableForView.table.id,
                        seatedBy: 'Super Admin',
                        seatNumber: selectedSeat ? parseInt(selectedSeat, 10) : undefined
                      });
                      setSelectedTableForView(null);
                      setSelectedSeat('');
                    }}
                    disabled={!selectedSeat || assignSeatMutation.isLoading}
                    className="w-full bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    {assignSeatMutation.isLoading ? 'Assigning...' : `Assign to Seat ${selectedSeat || '...'}`}
                  </button>
                ) : (
                  <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    View-only access for this role.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Table Buy-Out View Component
// ============================================================================
function TableBuyOutView({ selectedClubId, tables }) {
  const [activeSubTab, setActiveSubTab] = useState("process-buyout");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedTableForBuyOut, setSelectedTableForBuyOut] = useState("");
  const [buyOutReason, setBuyOutReason] = useState("");
  const [buyOutAmount, setBuyOutAmount] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  
  console.log('[PROCESS BUY-OUT] Tables available:', tables);
  console.log('[PROCESS BUY-OUT] Tables with players:', tables.filter(t => t.currentSeats > 0));

  // Fetch seated players for selected table
  const { data: seatedPlayersForTable = [], isLoading: seatedPlayersLoading } = useQuery({
    queryKey: ['seatedPlayersForTable', selectedClubId, selectedTableForBuyOut],
    queryFn: async () => {
      if (!selectedClubId || !selectedTableForBuyOut) return [];
      const response = await tablesAPI.getSeatedPlayersForTable(selectedClubId, selectedTableForBuyOut);
      return response?.seatedPlayers || [];
    },
    enabled: !!selectedClubId && !!selectedTableForBuyOut && activeSubTab === 'process-buyout',
  });

  // Handle buy-out approval
  const handleApproveBuyOut = async () => {
    if (!selectedPlayer) {
      toast.error('Please select a player');
      return;
    }
    const raw = String(buyOutAmount ?? '').trim();
    if (raw === '') {
      toast.error('Enter buy-out amount (use 0 if no chips to return)');
      return;
    }
    const amount = parseFloat(raw);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount (0 or greater)');
      return;
    }

    const confirmMsg =
      amount === 0
        ? `Confirm: Complete buy-out for ${selectedPlayer.playerName} with ₹0 chip return to wallet? Credit owed will still be settled from the wallet if applicable.`
        : `Confirm: Add ₹${amount.toLocaleString('en-IN')} to ${selectedPlayer.playerName}'s balance and complete buy-out?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const tableRow = tables.find((t) => t.id === selectedTableForBuyOut);
      const tableNumber = tableRow?.tableNumber;
      if (tableNumber == null || Number.isNaN(Number(tableNumber))) {
        toast.error('Could not resolve table number for buy-out');
        return;
      }

      const result = await superAdminAPI.manualTableBuyOut(selectedClubId, {
        playerId: selectedPlayer.playerId,
        tableNumber: Number(tableNumber),
        amount,
        reason: buyOutReason || undefined,
      });

      const creditSettled = result?.creditSettled ?? 0;
      toast.success(
        creditSettled > 0
          ? `Buy-out complete for ${selectedPlayer.playerName}: ₹${amount.toLocaleString('en-IN')} chips to wallet; ₹${Number(creditSettled).toLocaleString('en-IN')} credit settled (wallet may show negative until repaid).`
          : `Buy-out complete for ${selectedPlayer.playerName}: ₹${amount.toLocaleString('en-IN')} returned to wallet.`,
      );
      
      // Reset form
      setSelectedPlayer(null);
      setBuyOutAmount('');
      setBuyOutReason('');
      
      // Refresh data
      queryClient.invalidateQueries(['seatedPlayersForTable']);
      queryClient.invalidateQueries(['tables']);
      queryClient.invalidateQueries(['waitlist']);
      
    } catch (error) {
      console.error('Error approving buy-out:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve buy-out');
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['seatedPlayersForTable'] }),
        queryClient.refetchQueries({ queryKey: ['tables', selectedClubId] }),
        queryClient.refetchQueries({ queryKey: ['waitlist', selectedClubId] }),
      ]);
      toast.success('Table Buy-Out data refreshed');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-600/30 via-red-500/20 to-rose-700/30 rounded-xl p-6 border border-orange-800/40">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Table Buy-Out</h2>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors"
          >
            {isRefreshing ? (
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <p className="text-gray-300 text-sm mb-6">
          Manage player buy-out requests and process manual buy-outs.
        </p>

        {/* Sub Tabs */}
        <div className="flex gap-2 border-b border-orange-800/40 pb-4 mb-6">
          <button
            onClick={() => setActiveSubTab("process-buyout")}
            className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
              activeSubTab === "process-buyout"
                ? "bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-lg"
                : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}
          >
            Process Buy-Out
          </button>
          <button
            onClick={() => setActiveSubTab("player-requests")}
            className={`px-5 py-2 rounded-t-lg font-semibold transition-all ${
              activeSubTab === "player-requests"
                ? "bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-lg"
                : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}
          >
            Player Requests
          </button>
        </div>

        {/* Sub Tab Content */}
        {activeSubTab === "process-buyout" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Process Buy-Out */}
            <div className="bg-white/10 p-5 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Process Buy-Out</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">1. Select Table</label>
                  <select 
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    value={selectedTableForBuyOut}
                    onChange={(e) => {
                      setSelectedTableForBuyOut(e.target.value);
                      setSelectedPlayer(null); // Reset player when table changes
                    }}
                  >
                    <option value="">-- Select Table --</option>
                    {tables.filter(t => (t.currentSeats || 0) > 0).map((table) => (
                      <option key={table.id} value={table.id}>
                        Table {table.tableNumber} ({table.currentSeats || 0} players)
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedTableForBuyOut && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">2. Select Seated Player</label>
                    <select 
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      value={selectedPlayer?.playerId || ''}
                      onChange={(e) => {
                        const player = seatedPlayersForTable.find(p => p.playerId === e.target.value);
                        setSelectedPlayer(player || null);
                      }}
                      disabled={seatedPlayersLoading || seatedPlayersForTable.length === 0}
                    >
                      <option value="">
                        {seatedPlayersLoading ? 'Loading...' : seatedPlayersForTable.length === 0 ? 'No seated players' : '-- Select Player --'}
                      </option>
                      {seatedPlayersForTable.map((player) => (
                        <option key={player.playerId} value={player.playerId}>
                          {player.playerName} - Seat #{player.seatNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {selectedPlayer && (
                  <>
                    <div className="p-4 bg-blue-500/20 rounded-lg border border-blue-400/30">
                      <div className="text-sm text-white space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Player:</span>
                          <span className="font-semibold">{selectedPlayer.playerName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Seat:</span>
                          <span className="font-semibold">#{selectedPlayer.seatNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Cash (on table):</span>
                          <span className="font-semibold text-emerald-200">
                            ₹{(Number(selectedPlayer.cashOnTableThisSession) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Credit (on table):</span>
                          <span className="font-semibold text-amber-200">
                            ₹{(Number(selectedPlayer.creditOnTableThisSession) || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-white/10 pt-2">
                          <span className="text-gray-300">Total (chips):</span>
                          <span className="font-semibold text-cyan-300">
                            ₹{(Number(selectedPlayer.buyInAmount) || 0).toLocaleString()}
                          </span>
                        </div>
                        {selectedPlayer.creditFacilityEnabled && Number(selectedPlayer.creditLineLimit) > 0 ? (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Total credit line:</span>
                              <span className="font-semibold text-sky-200">
                                ₹{(Number(selectedPlayer.creditLineLimit) || 0).toLocaleString()}
                            </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Credit used:</span>
                              <span className="font-semibold text-rose-300">
                                ₹{(Number(selectedPlayer.creditLineUsed) || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Credit on line:</span>
                              <span className="font-semibold text-amber-200">
                                ₹{(Number(selectedPlayer.creditLineOnLine) || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-300">Credit remaining:</span>
                              <span className="font-semibold text-emerald-300">
                                ₹{(Number(selectedPlayer.creditLineRemaining) || 0).toLocaleString()}
                              </span>
                            </div>
                          </>
                        ) : null}
                        {(Number(selectedPlayer.totalCredits) || 0) !== 0 && (
                          <div className="flex justify-between text-[11px]">
                            <span className="text-gray-400">Credit ledger net:</span>
                            <span className="font-medium text-yellow-200/90">
                              ₹{(Number(selectedPlayer.totalCredits) || 0).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">3. Final Balance / Buy-Out Amount</label>
                      <input
                        type="number"
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        placeholder="Enter final balance amount..."
                        value={buyOutAmount}
                        onChange={(e) => setBuyOutAmount(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Cash ₹{Number(selectedPlayer?.cashOnTableThisSession || 0).toLocaleString()} · Credit ₹
                        {Number(selectedPlayer?.creditOnTableThisSession || 0).toLocaleString()} · Total ₹
                        {Number(selectedPlayer?.buyInAmount || 0).toLocaleString()}
                        {selectedPlayer?.creditFacilityEnabled && Number(selectedPlayer?.creditLineLimit) > 0
                          ? ` · Line cap ₹${Number(selectedPlayer.creditLineLimit).toLocaleString()} (${Number(selectedPlayer.creditLineUsed || 0).toLocaleString()} drawn)`
                          : ''}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">4. Reason (Optional)</label>
                      <textarea
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        rows="3"
                        placeholder="Enter reason for buy-out (optional)..."
                        value={buyOutReason}
                        onChange={(e) => setBuyOutReason(e.target.value)}
                      />
                    </div>
                    <button 
                      onClick={handleApproveBuyOut}
                      disabled={
                        !selectedPlayer ||
                        String(buyOutAmount ?? '').trim() === '' ||
                        isNaN(parseFloat(String(buyOutAmount).trim())) ||
                        parseFloat(String(buyOutAmount).trim()) < 0
                      }
                      className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-3 rounded-lg font-semibold transition-colors"
                    >
                      Approve Buy-Out
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Buy-Out Information */}
            <div className="bg-white/10 p-5 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Buy-Out Information</h3>
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-2">How Buy-Out Works:</h4>
                  <ul className="text-sm text-gray-300 space-y-2 list-disc list-inside">
                    <li>Select a player who is currently seated at a table</li>
                    <li>Their table chip balance will be moved to their available balance</li>
                    <li>Player will be removed from the table (seat becomes available)</li>
                    <li>Player can then convert chips to real money at the cashier counter</li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-500/20 rounded-lg border border-yellow-400/30">
                  <h4 className="text-yellow-300 font-semibold mb-2">⚠️ Important Notes:</h4>
                  <ul className="text-sm text-yellow-200 space-y-1 list-disc list-inside">
                    <li>Buy-out approval is permanent and cannot be undone</li>
                    <li>Table balance will be immediately transferred to player's available balance</li>
                    <li>Player must go to cashier to convert chips to real money</li>
                  </ul>
                </div>

                <div className="p-4 bg-green-500/20 rounded-lg border border-green-400/30">
                  <div className="text-sm text-green-300">
                    <div className="font-semibold mb-1">Currently Seated Players:</div>
                    <div className="text-green-200">0 player(s) at tables</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "player-requests" && (
          <TableBuyOutManagement clubId={selectedClubId} gameType="poker" />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// History View Component (5th Tab)
// ============================================================================
function HistoryView({ selectedClubId, tables }) {
  const [historyFilters, setHistoryFilters] = useState({
    type: 'all', // 'all', 'buy-in', 'buy-out'
    search: '',
    tableNumber: 'all',
    page: 1,
    perPage: 10,
  });

  // Fetch all transactions for history
  const { data: allTransactions = [], isLoading: historyLoading } = useQuery({
    queryKey: ['transactions', selectedClubId],
    queryFn: async () => {
      if (!selectedClubId) return [];
      try {
        const response = await superAdminAPI.getTransactions(selectedClubId, {});
        console.log('[HISTORY] Raw transactions response:', response);
        console.log('[HISTORY] Response type:', typeof response, Array.isArray(response));
        
        // Handle different response formats
        let transactions = Array.isArray(response) ? response : (response?.data || response?.transactions || []);
        // Filter to only poker game_type transactions (exclude rummy)
        transactions = transactions.filter(t => t.game_type !== 'rummy');
        console.log('[HISTORY] Parsed poker transactions:', transactions.length, 'records');
        
        return transactions;
      } catch (error) {
        console.error('[HISTORY] Error fetching transactions:', error);
        return [];
      }
    },
    enabled: !!selectedClubId,
  });

  // Filter history
  const filteredHistory = allTransactions.filter(t => {
    const isBuyIn = ['Table Buy In', 'Buy In', 'Club Buy In', 'Credit'].includes(t.type);
    const isBuyOut = ['Table Buy Out', 'Club Buy Out', 'Debit'].includes(t.type) || 
                     ((t.type === 'Deposit' || t.type === 'Cashout') && 
                     (t.notes?.toLowerCase().includes('buy-out') || 
                      t.notes?.toLowerCase().includes('buyout') ||
                      t.description?.toLowerCase().includes('buy-out') ||
                      t.description?.toLowerCase().includes('buyout')));
    
    // Filter by type
    if (historyFilters.type === 'buy-in' && !isBuyIn) return false;
    if (historyFilters.type === 'buy-out' && !isBuyOut) return false;
    if (historyFilters.type === 'all' && !(isBuyIn || isBuyOut)) return false;
    
    // Table filter
    if (historyFilters.tableNumber !== 'all') {
      const tableMatch = t.notes?.includes(`Table ${historyFilters.tableNumber}`) || 
                        t.description?.includes(`Table ${historyFilters.tableNumber}`) ||
                        t.notes?.includes(`table ${historyFilters.tableNumber}`) ||
                        t.description?.includes(`table ${historyFilters.tableNumber}`);
      if (!tableMatch) return false;
    }
    
    // Search filter
    if (historyFilters.search) {
      const search = historyFilters.search.toLowerCase();
      const playerName = t.playerName || t.player_name || '';
      const email = t.email || '';
      return (
        playerName.toLowerCase().includes(search) ||
        email.toLowerCase().includes(search)
      );
    }
    
    return true;
  });
  
  console.log('[HISTORY] All transactions:', allTransactions.length);
  console.log('[HISTORY] Filtered:', filteredHistory.length, 'Filters:', historyFilters);

  // Paginate history
  const paginatedHistory = filteredHistory.slice(
    (historyFilters.page - 1) * historyFilters.perPage,
    historyFilters.page * historyFilters.perPage
  );
  
  const totalPages = Math.ceil(filteredHistory.length / historyFilters.perPage);
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/30 via-purple-500/20 to-pink-700/30 rounded-xl p-6 border border-indigo-800/40">
        <h2 className="text-2xl font-bold mb-4">Buy-In & Buy-Out History</h2>
        <p className="text-gray-300 text-sm mb-6">
          View all buy-in and buy-out transactions with advanced filters.
        </p>
          {/* Filters */}
          <div className="bg-white/10 p-5 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Transaction Type</label>
                <select 
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  value={historyFilters.type}
                  onChange={(e) => setHistoryFilters({...historyFilters, type: e.target.value, page: 1})}
                >
                  <option value="all">All Transactions</option>
                  <option value="buy-in">Buy-In Only</option>
                  <option value="buy-out">Buy-Out Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Table</label>
                <select 
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  value={historyFilters.tableNumber}
                  onChange={(e) => setHistoryFilters({...historyFilters, tableNumber: e.target.value, page: 1})}
                >
                  <option value="all">All Tables</option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.tableNumber}>
                      Table {table.tableNumber}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Search Player</label>
                <input 
                  type="text"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="Player name or email..."
                  value={historyFilters.search}
                  onChange={(e) => setHistoryFilters({...historyFilters, search: e.target.value, page: 1})}
                />
              </div>
          </div>
        </div>
        
        {/* History Table */}
        <div className="bg-white/10 p-5 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            Transaction History ({filteredHistory.length} records)
          </h3>
          
          {historyLoading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : paginatedHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No transactions found</div>
          ) : (
            <>
              <div className="space-y-3 mb-6">
                {paginatedHistory.map((transaction, idx) => {
                  const isBuyIn = ['Table Buy In', 'Buy In', 'Club Buy In', 'Credit'].includes(transaction.type);
                  const isBuyOut = ['Table Buy Out', 'Club Buy Out', 'Debit'].includes(transaction.type) ||
                                  ((transaction.type === 'Deposit' || transaction.type === 'Cashout') && 
                                  (transaction.notes?.toLowerCase().includes('buy-out') || 
                                   transaction.notes?.toLowerCase().includes('buyout') ||
                                   transaction.description?.toLowerCase().includes('buy-out') ||
                                   transaction.description?.toLowerCase().includes('buyout')));
                  
                  return (
                    <div 
                      key={transaction.id || idx} 
                      className={`p-4 rounded-lg border-2 ${
                        isBuyIn 
                          ? 'bg-green-900/20 border-green-600/50' 
                          : 'bg-orange-900/20 border-orange-600/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">
                              {isBuyIn ? '💰' : '💵'}
                            </span>
                            <div>
                              <div className="font-bold text-white">
                                {transaction.playerName || transaction.player_name || 'Unknown Player'}
                              </div>
                              <div className="text-sm text-gray-400">
                                {isBuyIn ? 'Buy-In' : 'Buy-Out'}
                                {transaction.notes &&
                                  ` • ${sanitizeTransactionNotesForDisplay(transaction.notes)}`}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(transaction.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={`text-right ${
                          isBuyIn ? 'text-green-400' : 'text-orange-400'
                        }`}>
                          <div className="font-bold text-2xl">
                            {isBuyIn ? '+' : ''}₹{Number(transaction.amount).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {transaction.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2">
                  <button
                    onClick={() => setHistoryFilters({...historyFilters, page: Math.max(1, historyFilters.page - 1)})}
                    disabled={historyFilters.page === 1}
                    className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
                  >
                    Previous
                  </button>
                  <span className="text-white">
                    Page {historyFilters.page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setHistoryFilters({...historyFilters, page: Math.min(totalPages, historyFilters.page + 1)})}
                    disabled={historyFilters.page === totalPages}
                    className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
